import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Button, Accordion } from 'react-bootstrap';
import {  FaChevronDown, FaChevronUp, FaFilter, FaSearch } from 'react-icons/fa';
import { motion } from 'framer-motion';
import 'bootstrap/dist/css/bootstrap.min.css';
import './projects.css'; // You would create this CSS file for additional styling

const ProjectsShowcase = () => {
  // State for storing projects, filtering, and animation
  const [projects, setProjects] = useState([]);
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [visibleProjects, setVisibleProjects] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  
  const projectRefs = useRef([]);
  
  // Sample categories - you would expand this
  const categories = [
    'All', 'Blockchain', 'Quantum Computing', 'AI', 'Cloud Computing', 
    'Data Science', 'Cybersecurity', 'Mobile Development', 'Web Development',
    '3D/VR', 'Product Management', 'E-commerce', 'Healthcare', 'Education',
    'Sustainability', 'Fintech', 'Energy','Geophysics','Geology', 'Pharmaceutical', 'Oil Industry'
  ];
  
  // Sample projects data - you would expand this to 150+ projects
  const allProjects = [
    {
        id: 1,
        title: "Decentralized Identity Management System",
        category: "Blockchain",
        description: "Create a blockchain-based identity management solution that allows users to control their digital identities without relying on centralized authorities. Students will learn about zero-knowledge proofs, DIDs (Decentralized Identifiers), and verifiable credentials with a focus on interoperability across multiple blockchain networks.",
        githubLink: "https://github.com/academy/decentralized-identity",
        scope: "2025-2027: Digital identity solutions will be crucial for Web3 adoption and compliance with evolving privacy regulations."
      },
      {
        id: 2,
        title: "Cross-Chain DeFi Aggregator",
        category: "Blockchain",
        description: "Build a DeFi application that aggregates liquidity and yield opportunities across multiple blockchain networks. Students will implement cross-chain bridges, smart contract development on multiple platforms, and optimal routing algorithms.",
        githubLink: "https://github.com/academy/cross-chain-defi",
        scope: "2025-2027: As the blockchain ecosystem becomes increasingly fragmented, cross-chain interoperability solutions will be in high demand."
      },
      {
        id: 3,
        title: "NFT-Based Intellectual Property Marketplace",
        category: "Blockchain",
        description: "Develop a platform for creators to tokenize, manage, and monetize their intellectual property rights using NFTs. The project covers royalty distribution mechanisms, programmable ownership rights, and integration with traditional IP legal frameworks.",
        githubLink: "https://github.com/academy/nft-ip-marketplace",
        scope: "2025-2027: Digital content ownership and creator economies will continue to evolve through blockchain technology."
      },
      {
        id: 4,
        title: "DAO Governance Framework",
        category: "Blockchain",
        description: "Design and implement a comprehensive governance system for Decentralized Autonomous Organizations. Students will create voting mechanisms, proposal systems, treasury management, and reputation systems while addressing governance attacks and vulnerabilities.",
        githubLink: "https://github.com/academy/dao-governance",
        scope: "2025-2027: DAOs will become mainstream organizational structures requiring sophisticated governance systems."
      },
      {
        id: 5,
        title: "Zero-Knowledge Supply Chain Verification",
        category: "Blockchain",
        description: "Build a supply chain tracking system that uses zero-knowledge proofs to verify product authenticity and provenance while maintaining confidentiality of sensitive business data. Students will implement ZK circuits and blockchain attestations.",
        githubLink: "https://github.com/academy/zk-supply-chain",
        scope: "2025-2027: Privacy-preserving blockchain solutions will be essential as regulatory scrutiny increases."
      },
      {
        id: 6,
        title: "Asset Tokenization Platform",
        category: "Blockchain",
        description: "Create a platform for tokenizing real-world assets like real estate, art, or commodities. The project covers regulatory compliance, fractional ownership, liquid secondary markets, and integration with legal frameworks.",
        githubLink: "https://github.com/academy/asset-tokenization",
        scope: "2025-2027: Tokenization of real-world assets will transform traditional financial markets."
      },
      {
        id: 7,
        title: "Decentralized Prediction Market",
        category: "Blockchain",
        description: "Develop a prediction market platform where users can create markets for future events and trade outcome tokens. Students will implement oracle systems, automated market makers, and incentive alignment mechanisms.",
        githubLink: "https://github.com/academy/prediction-market",
        scope: "2025-2027: Decentralized prediction markets will gain traction for crowd-forecasting complex events."
      },
      {
        id: 8,
        title: "Central Bank Digital Currency (CBDC) Prototype",
        category: "Blockchain",
        description: "Build a prototype for a programmable central bank digital currency with privacy features, regulatory controls, and offline transaction capabilities. Students will explore the balance between privacy and compliance in digital currencies.",
        githubLink: "https://github.com/academy/cbdc-prototype",
        scope: "2025-2027: Multiple countries will launch CBDCs, creating demand for technical expertise in this area."
      },
      {
        id: 9,
        title: "Decentralized Insurance Platform",
        category: "Blockchain",
        description: "Create a decentralized insurance platform that uses smart contracts for automated claims processing, risk pooling, and parametric insurance products. Students will implement cryptographic techniques for secure premium management and claims verification.",
        githubLink: "https://github.com/academy/defi-insurance",
        scope: "2025-2027: DeFi insurance products will mature to provide protection for evolving digital assets."
      },
      {
        id: 10,
        title: "Blockchain-Based Carbon Credit Trading",
        category: "Blockchain",
        description: "Develop a platform for tracking, verifying, and trading carbon credits on a blockchain. Students will implement verification protocols, market mechanisms, and integration with real-world carbon offsetting projects.",
        githubLink: "https://github.com/academy/carbon-credits",
        scope: "2025-2027: Environmental markets will increasingly rely on blockchain for transparency and efficiency."
      },
      {
        id: 11,
        title: "Soulbound Tokens Implementation",
        category: "Blockchain",
        description: "Build a system for creating and managing non-transferable tokens (Soulbound Tokens) that represent credentials, achievements, or reputation. Students will learn about token standards, on-chain verification, and privacy considerations.",
        githubLink: "https://github.com/academy/soulbound-tokens",
        scope: "2025-2027: Non-transferable tokens will become important for digital identity and reputation systems."
      },
      {
        id: 12,
        title: "Private Layer 2 Settlement System",
        category: "Blockchain",
        description: "Create a Layer 2 scaling solution with privacy features for blockchain transaction settlement. Students will implement optimistic rollups or ZK-rollups with a focus on transaction privacy and throughput.",
        githubLink: "https://github.com/academy/private-layer2",
        scope: "2025-2027: Blockchain scaling with privacy guarantees will be essential for enterprise adoption."
      },
      {
        id: 13,
        title: "Decentralized Data Marketplace",
        category: "Blockchain",
        description: "Develop a marketplace for data providers and consumers with tokenized access rights, data quality verification, and fair compensation mechanisms. Students will implement cryptographic techniques for secure data sharing and access control.",
        githubLink: "https://github.com/academy/data-marketplace",
        scope: "2025-2027: Data marketplaces will transform how organizations acquire and monetize information."
      },
      {
        id: 14,
        title: "Regenerative Finance (ReFi) Platform",
        category: "Blockchain",
        description: "Build a platform that incentivizes and finances environmental regeneration projects through tokenized impact investing. Students will implement impact verification, tokenomics design, and community governance.",
        githubLink: "https://github.com/academy/refi-platform",
        scope: "2025-2027: Blockchain-based climate finance solutions will scale to address environmental challenges."
      },
      {
        id: 15,
        title: "On-Chain Gaming Economy",
        category: "Blockchain",
        description: "Create an interoperable gaming economy with tradable in-game assets, reputation systems, and cross-game compatibility. Students will learn about ERC standards for gaming, game-theoretical token design, and anti-fraud mechanisms.",
        githubLink: "https://github.com/academy/gaming-economy",
        scope: "2025-2027: Blockchain gaming will evolve from pure speculation to sustainable economies."
      },
      
      // QUANTUM COMPUTING PROJECTS
      {
        id: 16,
        title: "Quantum Machine Learning Classifier",
        category: "Quantum Computing",
        description: "Implement quantum machine learning algorithms for classification tasks and compare performance with classical approaches. Students will learn about quantum feature maps, variational quantum circuits, and hybrid quantum-classical optimization.",
        githubLink: "https://github.com/academy/quantum-ml-classifier",
        scope: "2025-2027: Quantum ML will begin showing advantages in specific domains as quantum hardware improves."
      },
      {
        id: 17,
        title: "Quantum-Secure Cryptographic Protocol",
        category: "Quantum Computing",
        description: "Design and implement post-quantum cryptographic protocols resistant to quantum attacks. Students will learn about lattice-based cryptography, hash-based signatures, and quantum key distribution protocols.",
        githubLink: "https://github.com/academy/quantum-secure-crypto",
        scope: "2025-2027: As quantum computers advance, quantum-resistant cryptography will become essential for security."
      },
      {
        id: 18,
        title: "Quantum Algorithm for Optimization Problems",
        category: "Quantum Computing",
        description: "Develop quantum algorithms for solving complex optimization problems in logistics, finance, or materials science. Students will implement QAOA and VQE algorithms on quantum simulators and actual quantum hardware.",
        githubLink: "https://github.com/academy/quantum-optimization",
        scope: "2025-2027: Quantum optimization algorithms will begin delivering practical advantages in specific niches."
      },
      {
        id: 19,
        title: "Quantum Simulation of Chemical Reactions",
        category: "Quantum Computing",
        description: "Build quantum simulations of molecular systems to predict chemical reaction outcomes. Students will implement Hamiltonian simulation techniques and analyze molecular properties using quantum computing frameworks.",
        githubLink: "https://github.com/academy/quantum-chemistry",
        scope: "2025-2027: Quantum chemistry simulations will accelerate drug discovery and materials development."
      },
      {
        id: 20,
        title: "Quantum Error Correction Implementation",
        category: "Quantum Computing",
        description: "Design and implement quantum error correction codes to mitigate the effects of noise in quantum computations. Students will learn about stabilizer codes, surface codes, and fault-tolerant quantum computing techniques.",
        githubLink: "https://github.com/academy/quantum-error-correction",
        scope: "2025-2027: Error correction will be crucial for scaling quantum computers to practical applications."
      },
      {
        id: 21,
        title: "Quantum Financial Risk Analysis",
        category: "Quantum Computing",
        description: "Develop quantum algorithms for financial risk assessment and portfolio optimization. Students will implement quantum Monte Carlo methods and quantum linear algebra approaches for financial modeling.",
        githubLink: "https://github.com/academy/quantum-finance",
        scope: "2025-2027: Financial institutions will begin adopting quantum computing for specific risk calculations."
      },
      {
        id: 22,
        title: "Quantum-Classical Hybrid Neural Network",
        category: "Quantum Computing",
        description: "Create a hybrid quantum-classical neural network architecture for image recognition or natural language processing tasks. Students will explore quantum layers integrated with classical deep learning frameworks.",
        githubLink: "https://github.com/academy/hybrid-quantum-nn",
        scope: "2025-2027: Hybrid architectures will emerge as practical approaches to leverage quantum advantages."
      },
      {
        id: 23,
        title: "Quantum Internet Protocol",
        category: "Quantum Computing",
        description: "Design protocols for quantum networks enabling secure communication and distributed quantum computing. Students will implement quantum teleportation, quantum repeaters, and entanglement distribution protocols.",
        githubLink: "https://github.com/academy/quantum-internet",
        scope: "2025-2027: Early quantum networks will be established, requiring standardized protocols."
      },
      {
        id: 24,
        title: "Quantum Game Theory Simulator",
        category: "Quantum Computing",
        description: "Build a simulation platform for quantum game theory, exploring how quantum strategies can outperform classical approaches. Students will implement quantum games and analyze equilibria in quantum strategic interactions.",
        githubLink: "https://github.com/academy/quantum-game-theory",
        scope: "2025-2027: Quantum game theory will influence economic modeling and strategic decision-making."
      },
      {
        id: 25,
        title: "Quantum Database Query Acceleration",
        category: "Quantum Computing",
        description: "Implement quantum algorithms for accelerating database search and join operations. Students will develop quantum versions of classical database operations using Grover's algorithm and quantum machine learning techniques.",
        githubLink: "https://github.com/academy/quantum-database",
        scope: "2025-2027: Database operations will be among the first practical applications of quantum advantage."
      },
      
      // AI AND MACHINE LEARNING PROJECTS
      {
        id: 26,
        title: "Multimodal AI for Medical Diagnosis",
        category: "AI/ML",
        description: "Develop an AI system that integrates multiple data modalities (imaging, text, genomics) for comprehensive medical diagnostics. Students will implement cross-modal attention mechanisms and medical domain adaptation techniques.",
        githubLink: "https://github.com/academy/multimodal-medical-ai",
        scope: "2025-2027: Integrated multimodal AI will transform medical diagnostics with higher accuracy and explainability."
      },
      {
        id: 27,
        title: "Autonomous Drone Navigation System",
        category: "AI/ML",
        description: "Build an end-to-end autonomous navigation system for drones operating in complex environments. Students will implement vision-based navigation, reinforcement learning for path planning, and safety constraint satisfaction.",
        githubLink: "https://github.com/academy/autonomous-drone",
        scope: "2025-2027: Autonomous drone technology will expand into logistics, infrastructure inspection, and emergency response."
      },
      {
        id: 28,
        title: "AI-Based Code Generation and Optimization",
        category: "AI/ML",
        description: "Create an AI system that generates, optimizes, and verifies code based on natural language specifications. Students will fine-tune large language models for code generation and implement verification techniques.",
        githubLink: "https://github.com/academy/ai-code-generation",
        scope: "2025-2027: AI code assistants will evolve into full development partners for software engineers."
      },
      {
        id: 29,
        title: "Neuro-Symbolic AI Reasoning System",
        category: "AI/ML",
        description: "Develop a hybrid AI system that combines neural networks with symbolic reasoning for transparent and explainable AI decisions. Students will implement neural-symbolic integration and logical reasoning capabilities.",
        githubLink: "https://github.com/academy/neuro-symbolic-ai",
        scope: "2025-2027: Neuro-symbolic approaches will address explainability requirements in regulated industries."
      },
      {
        id: 30,
        title: "Few-Shot Learning for Computer Vision",
        category: "AI/ML",
        description: "Build a computer vision system capable of learning from very few examples. Students will implement meta-learning, contrastive learning, and prototype networks for efficient visual concept acquisition.",
        githubLink: "https://github.com/academy/few-shot-vision",
        scope: "2025-2027: Few-shot learning will enable AI adoption in domains with limited labeled data."
      },
      {
        id: 31,
        title: "Federated Learning Privacy Framework",
        category: "AI/ML",
        description: "Create a federated learning system with strong privacy guarantees for distributed model training. Students will implement differential privacy, secure aggregation, and attack mitigation techniques.",
        githubLink: "https://github.com/academy/federated-learning-privacy",
        scope: "2025-2027: Privacy-preserving ML will become essential as regulations around data usage tighten."
      },
      {
        id: 32,
        title: "AI Agent for Scientific Discovery",
        category: "AI/ML",
        description: "Develop an AI agent that autonomously generates and tests scientific hypotheses in a specific domain. Students will implement scientific knowledge representation, experimental design optimization, and causal reasoning.",
        githubLink: "https://github.com/academy/science-ai-agent",
        scope: "2025-2027: AI scientists will accelerate discovery in materials science, biology, and chemistry."
      },
      {
        id: 33,
        title: "Continuous Learning AI System",
        category: "AI/ML",
        description: "Build an AI system capable of continually learning from streaming data without catastrophic forgetting. Students will implement replay buffers, elastic weight consolidation, and experience rehearsal techniques.",
        githubLink: "https://github.com/academy/continuous-learning-ai",
        scope: "2025-2027: Continuous learning capabilities will be crucial for AI systems deployed in dynamic environments."
      },
      {
        id: 34,
        title: "Conversational AI for Mental Health Support",
        category: "AI/ML",
        description: "Create a conversational AI system designed to provide evidence-based mental health support. Students will implement emotion recognition, therapeutic dialogue generation, and ethical safeguards.",
        githubLink: "https://github.com/academy/mental-health-ai",
        scope: "2025-2027: AI mental health assistants will help address the global shortage of mental health resources."
      },
      {
        id: 35,
        title: "Reinforcement Learning for Energy Grid Optimization",
        category: "AI/ML",
        description: "Develop a reinforcement learning system that optimizes energy distribution in smart grids with renewable energy sources. Students will implement multi-agent RL, constraint satisfaction, and simulation environments.",
        githubLink: "https://github.com/academy/rl-energy-grid",
        scope: "2025-2027: RL-based grid management will be critical for integrating renewable energy sources at scale."
      },
      {
        id: 36,
        title: "Synthetic Media Detection System",
        category: "AI/ML",
        description: "Build an AI system for detecting AI-generated content across multiple modalities (text, images, audio, video). Students will implement digital fingerprinting, adversarial techniques, and multimodal fusion methods.",
        githubLink: "https://github.com/academy/synthetic-media-detection",
        scope: "2025-2027: Deepfake detection will be essential for maintaining trust in digital communications."
      },
      {
        id: 37,
        title: "AI for Personalized Education",
        category: "AI/ML",
        description: "Create an adaptive learning system that personalizes educational content and assessments based on individual learning patterns. Students will implement knowledge tracing, content recommendation, and learning optimization.",
        githubLink: "https://github.com/academy/personalized-education-ai",
        scope: "2025-2027: AI-driven personalization will transform educational outcomes across age groups."
      },
      {
        id: 38,
        title: "AI-Powered Robotic Manipulation",
        category: "AI/ML",
        description: "Develop an AI system for robotic manipulation of diverse objects in unstructured environments. Students will implement vision-based grasping, tactile feedback integration, and reinforcement learning for dexterity.",
        githubLink: "https://github.com/academy/robotic-manipulation",
        scope: "2025-2027: Advanced manipulation capabilities will expand robotics into new industrial and domestic applications."
      },
      {
        id: 39,
        title: "Causal Inference AI for Healthcare",
        category: "AI/ML",
        description: "Build an AI system that infers causal relationships from medical data to improve treatment planning. Students will implement causal discovery algorithms, counterfactual reasoning, and medical knowledge integration.",
        githubLink: "https://github.com/academy/causal-healthcare-ai",
        scope: "2025-2027: Causal AI will enable more precise personalized medicine and treatment planning."
      },
      {
        id: 40,
        title: "AI for Climate Change Prediction and Mitigation",
        category: "AI/ML",
        description: "Create an AI system for climate modeling, risk assessment, and mitigation planning. Students will implement physics-informed neural networks, time series forecasting, and decision optimization under uncertainty.",
        githubLink: "https://github.com/academy/climate-ai",
        scope: "2025-2027: AI climate models will provide actionable insights for policy and adaptation planning."
      },
      
      // CLOUD COMPUTING PROJECTS
      {
        id: 41,
        title: "Serverless Edge Computing Framework",
        category: "Cloud Computing",
        description: "Build a framework for deploying serverless functions across cloud and edge locations with intelligent workload distribution. Students will implement function deployment, state management, and latency optimization strategies.",
        githubLink: "https://github.com/academy/serverless-edge",
        scope: "2025-2027: Edge-cloud continuum computing will expand to support IoT and real-time applications."
      },
      {
        id: 42,
        title: "Multi-Cloud Orchestration Platform",
        category: "Cloud Computing",
        description: "Develop a platform for managing workloads across multiple cloud providers with policy-based governance. Students will implement multi-cloud resource management, cost optimization, and security controls.",
        githubLink: "https://github.com/academy/multi-cloud-orchestration",
        scope: "2025-2027: Multi-cloud strategies will become standard for enterprise resilience and optimization."
      },
      {
        id: 43,
        title: "Cloud-Native Observability System",
        category: "Cloud Computing",
        description: "Create a comprehensive observability solution for distributed cloud-native applications. Students will implement distributed tracing, metrics collection, anomaly detection, and visualization dashboards.",
        githubLink: "https://github.com/academy/cloud-observability",
        scope: "2025-2027: Advanced observability will be critical for managing increasingly complex distributed systems."
      },
      {
        id: 44,
        title: "Infrastructure as Code Security Scanner",
        category: "Cloud Computing",
        description: "Build a security analysis tool for infrastructure as code templates that identifies misconfigurations and vulnerabilities. Students will implement policy checking, compliance verification, and remediation recommendations.",
        githubLink: "https://github.com/academy/iac-security-scanner",
        scope: "2025-2027: Shifting security left in the infrastructure lifecycle will be essential for cloud security."
      },
      {
        id: 45,
        title: "Database-as-a-Service Platform",
        category: "Cloud Computing",
        description: "Develop a multi-tenant database service with automated scaling, backup, and performance optimization. Students will implement resource isolation, query optimization, and operational automation.",
        githubLink: "https://github.com/academy/dbaas-platform",
        scope: "2025-2027: Specialized database services will proliferate to address diverse data workloads."
      },
      {
        id: 46,
        title: "Cloud-Native AI/ML Pipeline",
        category: "Cloud Computing",
        description: "Create an end-to-end ML platform for model training, deployment, and monitoring in cloud environments. Students will implement feature stores, distributed training, model serving, and drift detection.",
        githubLink: "https://github.com/academy/cloud-ml-pipeline",
        scope: "2025-2027: Integrated MLOps platforms will become standard for enterprise AI deployment."
      },
      {
        id: 47,
        title: "Zero Trust Cloud Security Framework",
        category: "Cloud Computing",
        description: "Build a comprehensive zero trust security implementation for cloud environments. Students will implement identity verification, least privilege access, microsegmentation, and continuous monitoring.",
        githubLink: "https://github.com/academy/zero-trust-cloud",
        scope: "2025-2027: Zero trust architectures will become the standard approach to cloud security."
      },
      {
        id: 48,
        title: "Cloud Cost Optimization Engine",
        category: "Cloud Computing",
        description: "Develop an intelligent system for optimizing cloud resource utilization and costs. Students will implement resource rightsizing, spot instance management, and predictive scaling capabilities.",
        githubLink: "https://github.com/academy/cloud-cost-optimization",
        scope: "2025-2027: Cost optimization will be a critical discipline as cloud spending continues to grow."
      },
      {
        id: 49,
        title: "Hybrid Cloud Integration Platform",
        category: "Cloud Computing",
        description: "Create a platform for seamless integration between on-premises systems and multiple cloud environments. Students will implement secure connectivity, data synchronization, and hybrid workflow orchestration.",
        githubLink: "https://github.com/academy/hybrid-cloud-integration",
        scope: "2025-2027: Hybrid architectures will remain important for regulated industries and legacy systems."
      },
      {
        id: 50,
        title: "Cloud-Native Disaster Recovery System",
        category: "Cloud Computing",
        description: "Build an automated disaster recovery solution for cloud applications with minimal data loss and downtime. Students will implement multi-region replication, automated failover, and recovery testing.",
        githubLink: "https://github.com/academy/cloud-disaster-recovery",
        scope: "2025-2027: Enhanced resilience will be critical as more critical systems move to the cloud."
      },
      
      // ENERGY SECTOR PROJECTS
      {
        id: 51,
        title: "Smart Grid Demand Response Platform",
        category: "Energy",
        description: "Develop a platform for coordinating demand response across residential and commercial consumers in smart grids. Students will implement real-time pricing algorithms, load forecasting, and consumer engagement mechanisms.",
        githubLink: "https://github.com/academy/demand-response-platform",
        scope: "2025-2027: Demand response will be critical for grid stability with increased renewable penetration."
      },
      {
        id: 52,
        title: "Renewable Energy Forecasting System",
        category: "Energy",
        description: "Create an AI-powered forecasting system for solar and wind energy production using weather data and historical generation patterns. Students will implement ensemble forecasting models and uncertainty quantification.",
        githubLink: "https://github.com/academy/renewable-forecasting",
        scope: "2025-2027: Accurate renewable forecasting will enable higher renewable penetration in energy grids."
      },
      {
        id: 53,
        title: "Peer-to-Peer Energy Trading Platform",
        category: "Energy",
        description: "Build a platform enabling direct energy trading between prosumers with distributed energy resources. Students will implement pricing mechanisms, smart contract-based settlements, and grid integration protocols.",
        githubLink: "https://github.com/academy/p2p-energy-trading",
        scope: "2025-2027: Local energy markets will transform how energy is distributed and monetized."
      },
      {
        id: 54,
        title: "Building Energy Management System",
        category: "Energy",
        description: "Develop an intelligent system for optimizing energy usage in commercial buildings. Students will implement multi-objective optimization, IoT sensor integration, and predictive maintenance capabilities.",
        githubLink: "https://github.com/academy/building-energy-management",
        scope: "2025-2027: Smart buildings will be key to achieving energy efficiency targets and reducing costs."
      },
      {
        id: 55,
        title: "Virtual Power Plant Aggregation Platform",
        category: "Energy",
        description: "Create a system for aggregating and coordinating distributed energy resources to function as a virtual power plant. Students will implement resource coordination, grid service provision, and revenue optimization.",
        githubLink: "https://github.com/academy/virtual-power-plant",
        scope: "2025-2027: VPPs will become a standard approach to integrating distributed energy resources."
      },
      {
        id: 56,
        title: "Energy Storage Optimization System",
        category: "Energy",
        description: "Build a management system for optimizing the operation of grid-scale and distributed energy storage. Students will implement charge/discharge optimization, degradation modeling, and value stacking algorithms.",
        githubLink: "https://github.com/academy/energy-storage-optimization",
        scope: "2025-2027: Advanced storage management will be essential as storage deployment accelerates."
      },
      {
        id: 57,
        title: "EV Charging Infrastructure Management",
        category: "Energy",
        description: "Develop a platform for managing EV charging infrastructure with smart charging, grid integration, and user services. Students will implement charging optimization, load balancing, and payment systems.",
        githubLink: "https://github.com/academy/ev-charging-management",
        scope: "2025-2027: Intelligent EV charging networks will be critical for transportation electrification."
      },
      {
        id: 58,
        title: "Industrial Energy Efficiency Platform",
        category: "Energy",
        description: "Create a system for monitoring and optimizing energy usage in industrial processes. Students will implement digital twins, process optimization, and energy performance benchmarking capabilities.",
        githubLink: "https://github.com/academy/industrial-energy-efficiency",
        scope: "2025-2027: Industrial energy optimization will be key to meeting decarbonization targets."
      },
      {
        id: 59,
        title: "Microgrid Control System",
        category: "Energy",
        description: "Build a control system for managing microgrids with multiple generation sources, storage, and loads. Students will implement islanding capabilities, stability control, and economic dispatch algorithms.",
        githubLink: "https://github.com/academy/microgrid-control",
        scope: "2025-2027: Microgrids will enhance resilience and integrate renewable resources at the local level."
      },
      {
        id: 60,
        title: "Carbon Accounting and Management Platform",
        category: "Energy",
        description: "Develop a platform for tracking, reporting, and optimizing carbon emissions across organizational operations. Students will implement emissions calculation, reduction planning, and compliance reporting.",
        githubLink: "https://github.com/academy/carbon-management",
        scope: "2025-2027: Carbon management will become a core business function across industries."
      },
      
      // DATA SCIENCE PROJECTS
      {
        id: 61,
        title: "Anomaly Detection System for Network Security",
        category: "Data Science",
        description: "Build a machine learning system for detecting anomalous behavior in network traffic to identify security threats. Students will implement unsupervised learning, time series analysis, and real-time alerting capabilities.",
        githubLink: "https://github.com/academy/network-anomaly-detection",
        scope: "2025-2027: Advanced anomaly detection will be crucial for combating evolving cyber threats."
      },
      {
        id: 62,
        title: "Natural Language Processing for Legal Document Analysis",
        category: "Data Science",
        description: "Develop an NLP system for analyzing legal documents, extracting key information, and identifying potential issues. Students will implement named entity recognition, semantic analysis, and legal domain adaptation techniques.",
        githubLink: "https://github.com/academy/legal-nlp",
        scope: "2025-2027: NLP-powered legal tech will transform contract analysis and compliance workflows."
      },
      {
        id: 63,
        title: "Predictive Maintenance for Industrial Equipment",
        category: "Data Science",
        description: "Create a system that predicts equipment failures before they occur using sensor data and maintenance records. Students will implement time series forecasting, survival analysis, and explainable AI techniques.",
        githubLink: "https://github.com/academy/predictive-maintenance",
        scope: "2025-2027: Predictive maintenance will become standard across manufacturing and infrastructure."
      },
      {
        id: 64,
        title: "Customer Segmentation and Personalization Engine",
        category: "Data Science",
        description: "Build a system for dynamic customer segmentation and personalized recommendations across channels. Students will implement clustering algorithms, behavioral analysis, and reinforcement learning for optimization.",
        githubLink: "https://github.com/academy/customer-segmentation",
        scope: "2025-2027: Hyper-personalization will become expected across customer experiences."
      },
      {
        id: 65,
        title: "Supply Chain Optimization System",
        category: "Data Science",
        description: "Develop a data-driven system for optimizing supply chain operations from sourcing to delivery. Students will implement inventory optimization, route planning, and risk mitigation algorithms.",
        githubLink: "https://github.com/academy/supply-chain-optimization",
        scope: "2025-2027: Resilient and sustainable supply chains will be a competitive advantage across industries."
      },
      {
        id: 66,
        title: "Fraud Detection System for Financial Transactions",
        category: "Data Science",
        description: "Create a real-time fraud detection system for financial transactions using machine learning. Students will implement graph analytics, anomaly detection, and adaptive learning techniques to identify evolving fraud patterns.",
        githubLink: "https://github.com/academy/fraud-detection",
        scope: "2025-2027: Sophisticated fraud detection will be essential as digital transactions continue to grow."
      },
      {
        id: 67,
        title: "Sentiment Analysis for Brand Monitoring",
        category: "Data Science",
        description: "Build a comprehensive sentiment analysis system for monitoring brand perception across social media and news sources. Students will implement emotion detection, aspect-based sentiment analysis, and trend identification.",
        githubLink: "https://github.com/academy/brand-sentiment-analysis",
        scope: "2025-2027: Advanced sentiment analysis will become essential for brand management and crisis response."
      },
      {
        id: 68,
        title: "Time Series Forecasting for Business Planning",
        category: "Data Science",
        description: "Develop a forecasting system for key business metrics to support planning and decision-making. Students will implement state-of-the-art time series models, scenario analysis, and uncertainty quantification.",
        githubLink: "https://github.com/academy/business-forecasting",
        scope: "2025-2027: Probabilistic forecasting will transform business planning across industries."
      },
      {
        id: 69,
        title: "Healthcare Patient Risk Stratification",
        category: "Data Science",
        description: "Create a system for identifying high-risk patients and recommending preventive interventions. Students will implement survival analysis, comorbidity modeling, and personalized risk assessment.",
        githubLink: "https://github.com/academy/patient-risk-stratification",
        scope: "2025-2027: Predictive health analytics will shift healthcare toward prevention rather than treatment."
      },
      {
        id: 70,
        title: "Urban Traffic Flow Optimization",
        category: "Data Science",
        description: "Build a system for optimizing urban traffic flow using real-time data from sensors and connected vehicles. Students will implement reinforcement learning for traffic signal control and congestion prediction.",
        githubLink: "https://github.com/academy/traffic-optimization",
        scope: "2025-2027: Smart traffic management will be essential for sustainable urban mobility."
      },
      
      // CYBERSECURITY PROJECTS
      {
        id: 71,
        title: "Automated Threat Hunting Platform",
        category: "Cybersecurity",
        description: "Develop a system for proactively identifying security threats across an organization's digital infrastructure. Students will implement behavioral analytics, threat intelligence integration, and automated investigation workflows.",
        githubLink: "https://github.com/academy/threat-hunting",
        scope: "2025-2027: Proactive threat hunting will become standard practice for security operations."
      },
      {
        id: 72,
        title: "API Security Testing Framework",
        category: "Cybersecurity",
        description: "Create a comprehensive testing framework for identifying security vulnerabilities in APIs. Students will implement authentication testing, fuzzing, business logic testing, and compliance verification.",
        githubLink: "https://github.com/academy/api-security-testing",
        scope: "2025-2027: API security will be critical as APIs become the primary integration method."
      },
      {
        id: 73,
        title: "DevSecOps Pipeline Implementation",
        category: "Cybersecurity",
        description: "Build an end-to-end DevSecOps pipeline integrating security throughout the software development lifecycle. Students will implement code scanning, dependency analysis, container security, and compliance automation.",
        githubLink: "https://github.com/academy/devsecops-pipeline",
        scope: "2025-2027: Fully integrated DevSecOps will become essential for secure software delivery."
      },
      {
        id: 74,
        title: "Cloud Security Posture Management System",
        category: "Cybersecurity",
        description: "Develop a system for continuously monitoring and improving cloud security posture across multiple providers. Students will implement compliance monitoring, drift detection, and automated remediation.",
        githubLink: "https://github.com/academy/cloud-security-posture",
        scope: "2025-2027: Cloud security posture management will be critical as cloud environments grow in complexity."
      },
      {
        id: 75,
        title: "Privacy-Preserving Data Analytics Platform",
        category: "Cybersecurity",
        description: "Create a platform for analyzing sensitive data while preserving privacy. Students will implement differential privacy, homomorphic encryption, and secure multi-party computation techniques.",
        githubLink: "https://github.com/academy/privacy-preserving-analytics",
        scope: "2025-2027: Privacy-enhancing technologies will become essential for data analytics in regulated industries."
      },
      {
        id: 76,
        title: "Adversarial ML Defense Framework",
        category: "Cybersecurity",
        description: "Build a framework for protecting machine learning models against adversarial attacks. Students will implement adversarial training, input validation, model hardening, and detection of poisoning attacks.",
        githubLink: "https://github.com/academy/adversarial-ml-defense",
        scope: "2025-2027: Adversarial defenses will be essential as ML becomes integrated into critical systems."
      },
      {
        id: 77,
        title: "IoT Security Monitoring System",
        category: "Cybersecurity",
        description: "Develop a system for monitoring and protecting IoT devices across an organization. Students will implement device fingerprinting, behavioral analysis, and automated threat response capabilities.",
        githubLink: "https://github.com/academy/iot-security-monitoring",
        scope: "2025-2027: IoT security will become critical as billions of devices connect to enterprise networks."
      },
      {
        id: 78,
        title: "Secure Software Supply Chain Platform",
        category: "Cybersecurity",
        description: "Create a platform for ensuring the security of software components throughout the supply chain. Students will implement software bill of materials (SBOM) generation, vulnerability tracking, and provenance verification.",
        githubLink: "https://github.com/academy/secure-software-supply-chain",
        scope: "2025-2027: Software supply chain security will be a top priority for organizations and governments."
      },
      {
        id: 79,
        title: "Identity and Access Management System",
        category: "Cybersecurity",
        description: "Build a comprehensive IAM system with advanced authentication, authorization, and governance capabilities. Students will implement passwordless authentication, context-aware access policies, and compliance reporting.",
        githubLink: "https://github.com/academy/identity-access-management",
        scope: "2025-2027: Sophisticated IAM will be foundational for zero trust security architectures."
      },
      {
        id: 80,
        title: "Security Orchestration and Automation Platform",
        category: "Cybersecurity",
        description: "Develop a platform for automating security operations and incident response. Students will implement playbook automation, integration with security tools, and metrics for measuring effectiveness.",
        githubLink: "https://github.com/academy/security-automation",
        scope: "2025-2027: Security automation will be essential for handling the volume and complexity of modern threats."
      },
      
      // MOBILE DEVELOPMENT PROJECTS
      {
        id: 81,
        title: "Cross-Platform AR Shopping Experience",
        category: "Mobile Development",
        description: "Create an augmented reality shopping application that works across mobile platforms. Students will implement 3D product visualization, virtual try-on features, and spatial mapping for furniture placement.",
        githubLink: "https://github.com/academy/ar-shopping",
        scope: "2025-2027: AR shopping experiences will transform retail across product categories."
      },
      {
        id: 82,
        title: "Health and Wellness Tracking Platform",
        category: "Mobile Development",
        description: "Build a comprehensive health tracking application integrating with wearables and medical devices. Students will implement health data visualization, trend analysis, and personalized recommendations.",
        githubLink: "https://github.com/academy/health-tracking-app",
        scope: "2025-2027: Mobile health applications will become central to preventive healthcare and chronic disease management."
      },
      {
        id: 83,
        title: "Mobile-First Financial Management App",
        category: "Mobile Development",
        description: "Develop a financial management application optimized for mobile users with budgeting, investment tracking, and financial planning features. Students will implement secure financial data integration and personalized insights.",
        githubLink: "https://github.com/academy/mobile-finance-app",
        scope: "2025-2027: Mobile-first financial applications will drive financial inclusion and literacy."
      },
      {
        id: 84,
        title: "On-Device ML for Mobile Applications",
        category: "Mobile Development",
        description: "Create mobile applications that leverage on-device machine learning for features like image recognition, language processing, and personalization. Students will optimize ML models for mobile performance and privacy.",
        githubLink: "https://github.com/academy/on-device-ml",
        scope: "2025-2027: On-device AI will enable new mobile capabilities while preserving privacy and reducing latency."
      },
      {
        id: 85,
        title: "Offline-First Mobile Collaboration Tool",
        category: "Mobile Development",
        description: "Build a collaboration application that works seamlessly offline with conflict resolution and synchronization. Students will implement offline data storage, change tracking, and conflict resolution strategies.",
        githubLink: "https://github.com/academy/offline-collaboration-app",
        scope: "2025-2027: Offline-first applications will improve productivity in areas with limited connectivity."
      },
      {
        id: 86,
        title: "Location-Based Services Platform",
        category: "Mobile Development",
        description: "Develop a platform for creating location-aware applications with geofencing, activity recognition, and nearby discovery. Students will implement location tracking, spatial databases, and location-based recommendations.",
        githubLink: "https://github.com/academy/location-services-platform",
        scope: "2025-2027: Context-aware mobile applications will transform how users interact with physical spaces."
      },
      {
        id: 87,
        title: "Cross-Platform Mobile Game Engine",
        category: "Mobile Development",
        description: "Create a game development framework for building performant cross-platform mobile games. Students will implement rendering optimization, physics simulation, and cross-platform asset management.",
        githubLink: "https://github.com/academy/mobile-game-engine",
        scope: "2025-2027: Mobile gaming will continue to grow with increasingly sophisticated experiences."
      },
      {
        id: 88,
        title: "Mobile IoT Control Center",
        category: "Mobile Development",
        description: "Build a mobile application for monitoring and controlling IoT devices across home and industrial environments. Students will implement device discovery, secure control protocols, and automation scripting.",
        githubLink: "https://github.com/academy/mobile-iot-control",
        scope: "2025-2027: Mobile applications will become the primary interface for IoT ecosystem management."
      },
      {
        id: 89,
        title: "Accessibility-First Mobile Design System",
        category: "Mobile Development",
        description: "Develop a comprehensive design system and component library for creating fully accessible mobile applications. Students will implement WCAG compliance, assistive technology integration, and automated testing.",
        githubLink: "https://github.com/academy/accessibility-design-system",
        scope: "2025-2027: Accessibility will become a core requirement for mobile applications across industries."
      },
      {
        id: 90,
        title: "Mobile Digital Identity Wallet",
        category: "Mobile Development",
        description: "Create a secure mobile wallet for managing digital identities, credentials, and access rights. Students will implement secure credential storage, biometric authentication, and privacy-preserving verification protocols.",
        githubLink: "https://github.com/academy/digital-identity-wallet",
        scope: "2025-2027: Mobile identity wallets will transform authentication and verification across services."
      },
      
      // WEB DEVELOPMENT PROJECTS
      {
        id: 91,
        title: "Headless CMS with Composable Frontend",
        category: "Web Development",
        description: "Build a content management system with a headless architecture and composable frontend components. Students will implement content modeling, API design, and a component-based frontend framework.",
        githubLink: "https://github.com/academy/headless-cms",
        scope: "2025-2027: Composable architecture will become the standard approach for complex web applications."
      },
      {
        id: 92,
        title: "Real-time Collaboration Platform",
        category: "Web Development",
        description: "Develop a web-based collaboration platform with real-time document editing and communication. Students will implement operational transforms, presence indicators, and synchronization protocols.",
        githubLink: "https://github.com/academy/realtime-collaboration",
        scope: "2025-2027: Real-time collaboration will become expected across web applications and services."
      },
      {
        id: 93,
        title: "Progressive Web Application Framework",
        category: "Web Development",
        description: "Create a framework for building progressive web applications with offline capabilities and native-like features. Students will implement service workers, caching strategies, and background synchronization.",
        githubLink: "https://github.com/academy/pwa-framework",
        scope: "2025-2027: PWAs will continue to narrow the gap between web and native application experiences."
      },
      {
        id: 94,
        title: "Micro-Frontend Architecture Implementation",
        category: "Web Development",
        description: "Build a web application using micro-frontend architecture for independent development and deployment of features. Students will implement module federation, shared dependencies, and cross-team coordination.",
        githubLink: "https://github.com/academy/micro-frontend",
        scope: "2025-2027: Micro-frontend architectures will be adopted for complex enterprise applications."
      },
      {
        id: 95,
        title: "WebAssembly Application Framework",
        category: "Web Development",
        description: "Develop a framework for building high-performance web applications using WebAssembly. Students will implement language interoperability, memory management, and integration with web APIs.",
        githubLink: "https://github.com/academy/wasm-framework",
        scope: "2025-2027: WebAssembly will enable new categories of performance-critical web applications."
      },
      {
        id: 96,
        title: "Accessibility Testing and Remediation Platform",
        category: "Web Development",
        description: "Create a comprehensive platform for testing web accessibility and guiding remediation efforts. Students will implement automated testing, user flow analysis, and remediation prioritization.",
        githubLink: "https://github.com/academy/accessibility-platform",
        scope: "2025-2027: Accessibility will become a core requirement for web applications across industries."
      },
      {
        id: 97,
        title: "GraphQL API Gateway",
        category: "Web Development",
        description: "Build an API gateway using GraphQL to unify multiple backend services and data sources. Students will implement schema stitching, caching strategies, and authentication/authorization mechanisms.",
        githubLink: "https://github.com/academy/graphql-gateway",
        scope: "2025-2027: GraphQL will become the dominant approach for building flexible, efficient APIs."
      },
      {
        id: 98,
        title: "Web Performance Optimization Framework",
        category: "Web Development",
        description: "Develop a framework for measuring and optimizing web application performance. Students will implement performance budgeting, Core Web Vitals optimization, and automated performance testing.",
        githubLink: "https://github.com/academy/web-performance",
        scope: "2025-2027: Web performance will be a critical differentiator for user experience and business outcomes."
      },
      {
        id: 99,
        title: "Server-Side Rendering Framework",
        category: "Web Development",
        description: "Create a framework for server-side rendering of web applications with hydration and partial rerendering. Students will implement rendering strategies, state management, and SEO optimization.",
        githubLink: "https://github.com/academy/ssr-framework",
        scope: "2025-2027: Advanced server rendering approaches will balance performance and interactivity."
      },
      {
        id: 100,
        title: "Web3 Frontend Framework",
        category: "Web Development",
        description: "Build a frontend framework for developing decentralized web applications. Students will implement wallet integration, smart contract interaction, and distributed storage access.",
        githubLink: "https://github.com/academy/web3-frontend",
        scope: "2025-2027: Web3 interfaces will evolve to provide seamless user experiences for decentralized applications."
      },
      
      // 3D AND VIRTUAL REALITY PROJECTS
      {
        id: 101,
        title: "WebXR Virtual Collaboration Environment",
        category: "3D/VR",
        description: "Create a virtual reality environment for remote collaboration using WebXR. Students will implement avatar systems, shared workspaces, and spatial audio for immersive communication.",
        githubLink: "https://github.com/academy/webxr-collaboration",
        scope: "2025-2027: Virtual collaboration environments will enable new forms of remote teamwork."
      },
      {
        id: 102,
        title: "Digital Twin Visualization Platform",
        category: "3D/VR",
        description: "Develop a platform for creating and visualizing digital twins of physical systems with real-time data integration. Students will implement 3D modeling, simulation, and IoT data visualization.",
        githubLink: "https://github.com/academy/digital-twin-platform",
        scope: "2025-2027: Digital twins will transform how organizations manage physical assets and processes."
      },
      {
        id: 103,
        title: "AR Navigation and Information System",
        category: "3D/VR",
        description: "Build an augmented reality system for navigating physical spaces and accessing contextual information. Students will implement spatial mapping, visual positioning, and information overlay techniques.",
        githubLink: "https://github.com/academy/ar-navigation",
        scope: "2025-2027: AR navigation will enhance how users interact with physical environments."
      },
      {
        id: 104,
        title: "3D Model Optimization Framework",
        category: "3D/VR",
        description: "Create a framework for optimizing 3D models for web and mobile applications. Students will implement level-of-detail generation, texture compression, and geometry simplification techniques.",
        githubLink: "https://github.com/academy/3d-optimization",
        scope: "2025-2027: Efficient 3D content delivery will be critical for immersive applications at scale."
      },
      {
        id: 105,
        title: "VR Training Simulation Platform",
        category: "3D/VR",
        description: "Develop a platform for creating immersive training simulations in virtual reality. Students will implement interactive physics, performance assessment, and adaptive training scenarios.",
        githubLink: "https://github.com/academy/vr-training-platform",
        scope: "2025-2027: VR training will be widely adopted for complex and high-risk skills development."
      },
      
      // PRODUCT MANAGEMENT PROJECTS
      {
        id: 106,
        title: "Product Analytics Dashboard",
        category: "Product Management",
        description: "Build a comprehensive analytics dashboard for product managers to track metrics, user behavior, and feature performance. Students will implement analytics data collection, visualization, and insight generation.",
        githubLink: "https://github.com/academy/product-analytics",
        scope: "2025-2027: Data-driven product management will become standard practice across industries."
      },
      {
        id: 107,
        title: "User Research and Testing Platform",
        category: "Product Management",
        description: "Create a platform for conducting user research and usability testing at scale. Students will implement participant recruitment, test design, qualitative and quantitative analysis tools.",
        githubLink: "https://github.com/academy/user-research-platform",
        scope: "2025-2027: Continuous user research will be integrated throughout the product development lifecycle."
      },
      {
        id: 108,
        title: "Product Roadmap and Strategy Tool",
        category: "Product Management",
        description: "Develop a tool for creating and managing product roadmaps aligned with business strategy. Students will implement opportunity scoring, resource planning, and stakeholder communication features.",
        githubLink: "https://github.com/academy/product-roadmap-tool",
        scope: "2025-2027: Strategic product management tools will be essential for aligning product and business outcomes."
      },
      {
        id: 109,
        title: "Experimentation Platform for Product Teams",
        category: "Product Management",
        description: "Build a platform for designing, running, and analyzing product experiments. Students will implement A/B testing, feature flagging, statistical analysis, and experiment governance.",
        githubLink: "https://github.com/academy/experimentation-platform",
        scope: "2025-2027: Experimentation will become a core capability for product development across industries."
      },
      {
        id: 110,
        title: "Customer Feedback Management System",
        category: "Product Management",
        description: "Create a system for collecting, analyzing, and acting on customer feedback from multiple channels. Students will implement sentiment analysis, feedback categorization, and prioritization algorithms.",
        githubLink: "https://github.com/academy/feedback-management",
        scope: "2025-2027: Systematic feedback management will be essential for customer-centric product development."
      },
      
      // E-COMMERCE PROJECTS
      {
        id: 111,
        title: "Headless E-commerce Platform",
        category: "E-commerce",
        description: "Build a headless e-commerce platform with API-first architecture and composable commerce capabilities. Students will implement product management, order processing, and integration with frontend frameworks.",
        githubLink: "https://github.com/academy/headless-ecommerce",
        scope: "2025-2027: Headless architecture will become the standard approach for flexible e-commerce systems."
      },
      {
        id: 112,
        title: "Personalized Shopping Experience Engine",
        category: "E-commerce",
        description: "Develop an engine for delivering personalized product recommendations and shopping experiences. Students will implement collaborative filtering, behavioral analysis, and real-time personalization.",
        githubLink: "https://github.com/academy/personalized-shopping",
        scope: "2025-2027: Hyper-personalization will be a competitive differentiator in e-commerce."
      },
      {
        id: 113,
        title: "Omnichannel Retail Integration Platform",
        category: "E-commerce",
        description: "Create a platform for integrating online and offline retail channels with unified inventory, pricing, and customer experiences. Students will implement inventory synchronization, order routing, and channel analytics.",
        githubLink: "https://github.com/academy/omnichannel-retail",
        scope: "2025-2027: Seamless omnichannel experiences will be expected by consumers across retail categories."
      },
      {
        id: 114,
        title: "E-commerce Search and Discovery Engine",
        category: "E-commerce",
        description: "Build an advanced search and discovery engine for e-commerce with semantic understanding and personalized results. Students will implement vector search, query understanding, and ranking algorithms.",
        githubLink: "https://github.com/academy/ecommerce-search",
        scope: "2025-2027: Sophisticated search capabilities will significantly impact conversion rates and customer satisfaction."
      },
      {
        id: 115,
        title: "AI-Powered Product Photography System",
        category: "E-commerce",
        description: "Develop a system for automatically generating and enhancing product images for e-commerce. Students will implement image generation, background removal, and style transfer techniques.",
        githubLink: "https://github.com/academy/ai-product-photography",
        scope: "2025-2027: AI-generated product imagery will transform e-commerce visual merchandising."
      },
      
      // HEALTHCARE PROJECTS
      {
        id: 116,
        title: "Remote Patient Monitoring Platform",
        category: "Healthcare",
        description: "Create a platform for monitoring patients remotely using wearable devices and telehealth integration. Students will implement vital sign monitoring, alert systems, and clinical workflow integration.",
        githubLink: "https://github.com/academy/remote-patient-monitoring",
        scope: "2025-2027: Remote monitoring will become standard practice for chronic disease management."
      },
      {
        id: 117,
        title: "Medical Image Analysis System",
        category: "Healthcare",
        description: "Build a system for analyzing medical images to assist with diagnosis and treatment planning. Students will implement image segmentation, anomaly detection, and integration with clinical workflows.",
        githubLink: "https://github.com/academy/medical-image-analysis",
        scope: "2025-2027: AI-assisted image analysis will become standard in radiology and pathology."
      },
      {
        id: 118,
        title: "Clinical Decision Support System",
        category: "Healthcare",
        description: "Develop a decision support system for healthcare providers using patient data and medical knowledge bases. Students will implement evidence-based recommendations, risk assessment, and explanation generation.",
        githubLink: "https://github.com/academy/clinical-decision-support",
        scope: "2025-2027: AI-assisted clinical decision support will improve healthcare outcomes and efficiency."
      },
      {
        id: 119,
        title: "Healthcare Data Interoperability Platform",
        category: "Healthcare",
        description: "Create a platform for enabling interoperability between healthcare systems using FHIR and other standards. Students will implement data mapping, consent management, and secure data exchange.",
        githubLink: "https://github.com/academy/healthcare-interoperability",
        scope: "2025-2027: Healthcare data interoperability will be essential for coordinated, patient-centered care."
      },
      {
        id: 120,
        title: "Personal Health Record System",
        category: "Healthcare",
        description: "Build a patient-centered health record system with data aggregation from multiple sources and patient control. Students will implement secure data storage, provider sharing, and health tracking features.",
        githubLink: "https://github.com/academy/personal-health-record",
        scope: "2025-2027: Patient-controlled health records will transform healthcare data ownership and access."
      },
      
      // EDUCATION TECHNOLOGY PROJECTS
      {
        id: 121,
        title: "Adaptive Learning Platform",
        category: "Education",
        description: "Develop an adaptive learning platform that personalizes educational content based on learner progress and preferences. Students will implement knowledge mapping, learning path generation, and mastery assessment.",
        githubLink: "https://github.com/academy/adaptive-learning",
        scope: "2025-2027: Adaptive learning will transform educational outcomes across age groups and subjects."
      },
      {
        id: 122,
        title: "Collaborative Learning Environment",
        category: "Education",
        description: "Create a platform for collaborative learning with real-time interaction and project-based activities. Students will implement collaborative document editing, peer feedback systems, and team analytics.",
        githubLink: "https://github.com/academy/collaborative-learning",
        scope: "2025-2027: Collaborative digital learning will become central to education at all levels."
      },
      {
        id: 123,
        title: "Educational Assessment and Analytics Platform",
        category: "Education",
        description: "Build a comprehensive system for creating assessments, analyzing results, and generating insights on learning. Students will implement item analysis, mastery tracking, and personalized feedback generation.",
        githubLink: "https://github.com/academy/assessment-analytics",
        scope: "2025-2027: Data-driven assessment will transform how learning is measured and improved."
      },
      {
        id: 124,
        title: "AR/VR Learning Experience Platform",
        category: "Education",
        description: "Develop a platform for creating immersive learning experiences in augmented and virtual reality. Students will implement interactive 3D content, spatial learning activities, and progress tracking.",
        githubLink: "https://github.com/academy/xr-learning",
        scope: "2025-2027: Immersive learning experiences will transform education for complex subjects and skills."
      },
      {
        id: 125,
        title: "Microlearning Content Creation System",
        category: "Education",
        description: "Create a system for developing and delivering bite-sized learning content optimized for retention. Students will implement content atomization, spaced repetition, and microlearning analytics.",
        githubLink: "https://github.com/academy/microlearning",
        scope: "2025-2027: Microlearning approaches will transform corporate training and lifelong learning."
      },
      
      // SUSTAINABILITY PROJECTS
      {
        id: 126,
        title: "Sustainable Supply Chain Tracking System",
        category: "Sustainability",
        description: "Build a system for tracking sustainability metrics across complex supply chains with verification and reporting capabilities. Students will implement materials tracking, emissions calculation, and blockchain verification.",
        githubLink: "https://github.com/academy/sustainable-supply-chain",
        scope: "2025-2027: Transparent sustainability tracking will become essential for consumer trust and compliance."
      },
      {
        id: 127,
        title: "Environmental Impact Assessment Platform",
        category: "Sustainability",
        description: "Develop a platform for assessing the environmental impact of products and services throughout their lifecycle. Students will implement lifecycle analysis, carbon footprinting, and impact visualization.",
        githubLink: "https://github.com/academy/environmental-impact",
        scope: "2025-2027: Comprehensive impact assessment will be required for products across industries."
      },
      {
        id: 128,
        title: "Circular Economy Marketplace",
        category: "Sustainability",
        description: "Create a marketplace for circular economy products, materials, and services. Students will implement material passports, product lifecycle tracking, and value retention calculations.",
        githubLink: "https://github.com/academy/circular-economy",
        scope: "2025-2027: Circular economy business models will expand across industries to reduce waste."
      },
      {
        id: 129,
        title: "Biodiversity Monitoring and Conservation Platform",
        category: "Sustainability",
        description: "Build a platform for monitoring biodiversity using sensor networks, citizen science, and AI analysis. Students will implement species identification, habitat mapping, and conservation planning tools.",
        githubLink: "https://github.com/academy/biodiversity-platform",
        scope: "2025-2027: Digital tools will transform biodiversity conservation efforts worldwide."
      },
      {
        id: 130,
        title: "Urban Sustainability Dashboard",
        category: "Sustainability",
        description: "Develop a dashboard for tracking and improving urban sustainability metrics across energy, water, waste, and transportation. Students will implement data integration, visualization, and intervention planning.",
        githubLink: "https://github.com/academy/urban-sustainability",
        scope: "2025-2027: Data-driven urban management will be essential for sustainable cities."
      },
      
      // FINTECH PROJECTS
      {
        id: 131,
        title: "Open Banking Integration Platform",
        category: "Fintech",
        description: "Create a platform for integrating with open banking APIs to enable financial innovation. Students will implement secure authentication, data aggregation, and financial insights generation.",
        githubLink: "https://github.com/academy/open-banking",
        scope: "2025-2027: Open banking will transform financial services with new business models and capabilities."
      },
      {
        id: 132,
        title: "AI-Powered Financial Advisor",
        category: "Fintech",
        description: "Build an AI system that provides personalized financial advice based on individual financial data and goals. Students will implement financial planning algorithms, risk assessment, and natural language interaction.",
        githubLink: "https://github.com/academy/ai-financial-advisor",
        scope: "2025-2027: AI advisors will democratize access to financial guidance and planning."
      },
      {
        id: 133,
        title: "Real-Time Payment Processing System",
        category: "Fintech",
        description: "Develop a system for processing payments in real-time across multiple payment methods and currencies. Students will implement transaction processing, fraud detection, and settlement reconciliation.",
        githubLink: "https://github.com/academy/realtime-payments",
        scope: "2025-2027: Real-time payments will become the standard expectation across financial transactions."
      },
    
    {
      id: 134,
      title: "Quantum Algorithm Simulator",
      category: "Quantum Computing",
      description: "Build a simulator for quantum algorithms that allows developers to test and optimize quantum code without quantum hardware access. Students will implement Shor's algorithm, Grover's algorithm, and quantum machine learning techniques with interactive visualizations.",
      githubLink: "https://github.com/academy/quantum-simulator",
      scope: "2025-2027: Quantum algorithm development will become essential as quantum computers reach practical advantage in specific domains."
    },
    {
      id: 135,
      title: "Blockchain Supply Chain Verification",
      category: "Blockchain",
      description: "Develop an end-to-end blockchain solution for supply chain verification with real-time tracking and tamper-proof history. Students will implement Solidity smart contracts, zero-knowledge proofs for privacy, and integration with IoT sensors.",
      githubLink: "https://github.com/academy/blockchain-supply-chain",
      scope: "2025-2027: Blockchain verification will be standard for regulated industries and premium consumer products."
    },
    {
      id: 136,
      title: "Generative AI Content Creation Platform",
      category: "AI",
      description: "Create a platform that uses multiple generative AI models to produce various content types with human-in-the-loop refinement. Students will implement fine-tuning pipelines, prompt engineering frameworks, and ethical content filtering.",
      githubLink: "https://github.com/academy/gen-ai-platform",
      scope: "2025-2027: Generative AI will transform creative workflows across industries with increasing quality and control."
    },
    {
      id: 137,
      title: "Serverless Microservices Architecture",
      category: "Cloud Computing",
      description: "Build a cloud-native application using serverless architecture and event-driven microservices. Students will implement infrastructure as code, event sourcing patterns, and observability solutions.",
      githubLink: "https://github.com/academy/serverless-microservices",
      scope: "2025-2027: Serverless architectures will become the default for new cloud applications across industries."
    },
    {
      id: 138,
      title: "Carbon Footprint Optimization System",
      category: "Energy",
      description: "Develop a system for monitoring and optimizing carbon footprint across operations with prediction and recommendation capabilities. Students will implement energy consumption modeling, carbon offset verification, and sustainability reporting.",
      githubLink: "https://github.com/academy/carbon-optimization",
      scope: "2025-2027: Carbon management systems will be essential for regulatory compliance and consumer expectations."
    },
    {
      id: 139,
      title: "Decentralized Finance Trading Platform",
      category: "Fintech",
      description: "Create a DeFi platform for trading assets, providing liquidity, and yield optimization with cross-chain capabilities. Students will implement smart contracts, AMM algorithms, and optimized gas strategies.",
      githubLink: "https://github.com/academy/defi-platform",
      scope: "2025-2027: DeFi will continue disrupting traditional finance with institutional adoption and regulatory frameworks."
    },
    {
      id: 140,
      title: "Personalized Medicine Analytics Platform",
      category: "Healthcare",
      description: "Build a platform for analyzing patient data to enable personalized treatment recommendations. Students will implement genomic data processing, medical knowledge graphs, and explainable AI for clinical insights.",
      githubLink: "https://github.com/academy/personalized-medicine",
      scope: "2025-2027: Personalized medicine will transform healthcare delivery with data-driven treatment optimization."
    },
    {
      id: 141,
      title: "Zero Trust Security Framework",
      category: "Cybersecurity",
      description: "Develop a comprehensive zero trust security implementation with continuous verification and least privilege access. Students will implement identity-based microsegmentation, behavior analytics, and automated response systems.",
      githubLink: "https://github.com/academy/zero-trust-security",
      scope: "2025-2027: Zero trust architectures will become the standard security approach for enterprises worldwide."
    },
    {
      id: 142,
      title: "Immersive Metaverse Development Kit",
      category: "3D/VR",
      description: "Create a development toolkit for building interconnected virtual worlds with shared assets and experiences. Students will implement 3D rendering optimization, spatial audio, and cross-platform avatar systems.",
      githubLink: "https://github.com/academy/metaverse-dev-kit",
      scope: "2025-2027: Metaverse technologies will transform social interaction, entertainment, and digital commerce."
    },
    {
      id: 143,
      title: "Smart Grid Optimization Platform",
      category: "Energy",
      description: "Build a platform for optimizing energy distribution in smart grids with renewable integration and demand response. Students will implement predictive load balancing, distributed energy resource management, and grid resilience algorithms.",
      githubLink: "https://github.com/academy/smart-grid-platform",
      scope: "2025-2027: Smart grid technologies will be critical for renewable energy integration and grid stability."
    },
    {
        id: 144,
        title: "Seismic Data AI Interpreter",
        category: "Geology",
        description: "Develop an AI system that can automatically interpret seismic data to identify subsurface structures, potential hydrocarbon reservoirs, and geological anomalies with higher accuracy than traditional methods.",
        githubLink: "https://github.com/academy/seismic-ai-interpreter",
        scope: "2025-2027: Advanced seismic interpretation will remain critical for resource exploration with growing AI capabilities."
      },
      {
        id: 145,
        title: "Geological Fault Prediction System",
        category: "Geology",
        description: "Create an AI model that predicts geological fault formations and movements based on historical seismic data, satellite imagery, and ground deformation measurements.",
        githubLink: "https://github.com/academy/geo-fault-predictor",
        scope: "2025-2026: Enhanced fault prediction will be essential for infrastructure planning and natural hazard mitigation."
      },
      {
        id: 146,
        title: "Mineral Identification via Deep Learning",
        category: "Geology",
        description: "Build a deep learning system that can identify minerals in rock samples from microscopic images, spectroscopic data, and chemical composition analysis.",
        githubLink: "https://github.com/academy/mineral-deep-identifier",
        scope: "2025-2027: Automated mineral identification will accelerate resource exploration and materials science research."
      },
      {
        id: 147,
        title: "Quantum-Enhanced Reservoir Simulation",
        category: "Geology",
        description: "Develop a quantum computing approach to geological reservoir simulation, allowing for more complex fluid dynamics and geological heterogeneity modeling than classical systems.",
        githubLink: "https://github.com/academy/quantum-reservoir-sim",
        scope: "2026-2027: Quantum computing will revolutionize reservoir modeling as quantum hardware matures."
      },
      {
        id: 148,
        title: "Landslide Risk Assessment AI",
        category: "Geology",
        description: "Create an AI system that integrates meteorological data, terrain models, soil characteristics, and vegetation cover to predict landslide risks in mountainous regions.",
        githubLink: "https://github.com/academy/landslide-risk-ai",
        scope: "2025-2026: Climate change is increasing landslide hazards, making predictive systems increasingly valuable."
      },
      {
        id: 149,
        title: "Autonomous Geological Mapping Drone",
        category: "Geology",
        description: "Design an AI-powered drone system that can autonomously map geological features, collect samples, and analyze terrain in remote or dangerous areas.",
        githubLink: "https://github.com/academy/geo-mapping-drone",
        scope: "2025-2027: Autonomous geological exploration will expand access to difficult terrain while reducing human risk."
      },
      {
        id: 150,
        title: "Stratigraphic Sequence Neural Network",
        category: "Geology",
        description: "Develop a neural network system that can identify and classify stratigraphic sequences from well logs, core data, and outcrop images for basin analysis.",
        githubLink: "https://github.com/academy/stratigraphic-nn",
        scope: "2025-2026: Basin analysis techniques will benefit from AI pattern recognition capabilities."
      },
      {
        id: 151,
        title: "Geological Time Series Forecaster",
        category: "Geology",
        description: "Build an AI system that analyzes geological time series data to forecast future trends in mineral deposition, erosion patterns, and landscape evolution.",
        githubLink: "https://github.com/academy/geo-time-forecaster",
        scope: "2025-2027: Long-term geological forecasting will inform sustainability planning and resource management."
      },
      {
        id: 152,
        title: "Groundwater Flow AI Simulator",
        category: "Geology",
        description: "Create an AI-enhanced model for simulating groundwater flow through complex geological formations, accounting for fractures, heterogeneity, and seasonal variations.",
        githubLink: "https://github.com/academy/groundwater-ai-sim",
        scope: "2025-2026: Water resource management will increasingly rely on advanced groundwater modeling."
      },
      {
        id: 153,
        title: "Metamorphic Process Quantum Simulator",
        category: "Geology",
        description: "Develop a quantum computing application to simulate complex metamorphic processes and mineral transformations under varying pressure and temperature conditions.",
        githubLink: "https://github.com/academy/metamorphic-quantum-sim",
        scope: "2026-2027: Quantum simulation will enable unprecedented modeling of complex geological processes."
      },
      {
        id: 154,
        title: "Volcanic Eruption Prediction System",
        category: "Geology",
        description: "Build an AI model that integrates seismic activity, gas emissions, ground deformation, and thermal data to predict volcanic eruptions with improved accuracy.",
        githubLink: "https://github.com/academy/volcano-prediction-ai",
        scope: "2025-2026: Enhanced eruption prediction will save lives and improve hazard management."
      },
      {
        id: 155,
        title: "Geological Carbon Sequestration Optimizer",
        category: "Geology",
        description: "Create an AI system that identifies optimal geological formations for carbon sequestration and models long-term carbon storage stability and capacity.",
        githubLink: "https://github.com/academy/geo-carbon-sequestration",
        scope: "2025-2027: Carbon capture and storage will become increasingly important for climate mitigation strategies."
      },
      {
        id: 156,
        title: "Ore Deposit Genesis AI",
        category: "Geology",
        description: "Develop a machine learning system that models ore deposit formation processes and predicts potential new deposit locations based on regional geological characteristics.",
        githubLink: "https://github.com/academy/ore-genesis-ai",
        scope: "2025-2026: AI-driven mineral exploration will become essential as easily accessible deposits are depleted."
      },
      {
        id: 157,
        title: "Tectonic Stress Analysis Platform",
        category: "Geology",
        description: "Build an AI platform that analyzes tectonic stress patterns from multiple data sources to predict fault reactivation, crustal deformation, and seismic hazards.",
        githubLink: "https://github.com/academy/tectonic-stress-ai",
        scope: "2025-2027: Understanding crustal stress will improve earthquake hazard assessment and infrastructure planning."
      },
      {
        id: 158,
        title: "Geological Remote Sensing Interpreter",
        category: "Geology",
        description: "Create an AI system that automatically interprets multispectral and hyperspectral satellite imagery to identify geological features, mineral deposits, and environmental changes.",
        githubLink: "https://github.com/academy/geo-remote-sensing-ai",
        scope: "2025-2026: Remote sensing interpretation will accelerate mineral exploration and environmental monitoring."
      },
      {
        id: 159,
        title: "Quantum Mineralogy Analyzer",
        category: "Geology",
        description: "Develop a quantum computing application for analyzing mineral structures, predicting stability, and modeling transformations under extreme conditions.",
        githubLink: "https://github.com/academy/quantum-mineralogy",
        scope: "2026-2027: Quantum computing will enable atomic-level mineral analysis beyond classical computational limits."
      },
      {
        id: 160,
        title: "Paleoclimate Reconstruction AI",
        category: "Geology",
        description: "Build an AI system that reconstructs paleoclimate conditions from geological proxies, including sedimentary records, ice cores, and fossil evidence.",
        githubLink: "https://github.com/academy/paleoclimate-ai",
        scope: "2025-2026: Climate change research will benefit from improved paleoclimate modeling."
      },
      {
        id: 161,
        title: "Geochemical Anomaly Detector",
        category: "Geology",
        description: "Create a machine learning system that identifies geochemical anomalies in soil, water, and rock samples to locate mineral deposits and environmental contaminants.",
        githubLink: "https://github.com/academy/geochemical-anomaly-ai",
        scope: "2025-2027: Advanced anomaly detection will enhance exploration success rates and environmental monitoring."
      },
      {
        id: 162,
        title: "Sedimentary Basin Evolution Modeler",
        category: "Geology",
        description: "Develop an AI-powered model that simulates the evolution of sedimentary basins over geological time, including deposition, subsidence, compaction, and diagenesis.",
        githubLink: "https://github.com/academy/basin-evolution-ai",
        scope: "2025-2027: Basin modeling will continue to be essential for hydrocarbon exploration and geological research."
      },
      {
        id: 163,
        title: "Rock Physics AI Predictor",
        category: "Geology",
        description: "Build a machine learning system that predicts rock physical properties from mineralogical composition, pore structure, and burial history for reservoir characterization.",
        githubLink: "https://github.com/academy/rock-physics-ai",
        scope: "2025-2026: Rock physics prediction will improve subsurface characterization for energy and water resources."
      },
      {
        id: 164,
        title: "Geological Field Assistant AR",
        category: "Geology",
        description: "Create an augmented reality system with AI capabilities that assists geologists in the field by identifying rocks, measuring structures, and overlaying geological maps on the landscape.",
        githubLink: "https://github.com/academy/geo-field-ar",
        scope: "2025-2027: AR field tools will transform geological mapping and fieldwork efficiency."
      },
      {
        id: 165,
        title: "Critical Mineral Assessment AI",
        category: "Geology",
        description: "Develop an AI system that assesses global critical mineral resources, supply chain vulnerabilities, and exploration potential based on geological and economic factors.",
        githubLink: "https://github.com/academy/critical-mineral-ai",
        scope: "2025-2027: Critical mineral supply security will remain a key concern for technology and defense sectors."
      },
      {
        id: 166,
        title: "Geological Natural Language Processor",
        category: "Geology",
        description: "Build an NLP system specialized in geological literature that can extract structured data from research papers, field notes, and historical reports for knowledge synthesis.",
        githubLink: "https://github.com/academy/geo-nlp",
        scope: "2025-2026: Knowledge extraction from geological literature will accelerate research and exploration."
      },
      {
        id: 167,
        title: "Salt Dome Identification AI",
        category: "Geology",
        description: "Create a deep learning system that automatically identifies salt domes and related structures in seismic data to improve hydrocarbon exploration efficiency.",
        githubLink: "https://github.com/academy/salt-dome-ai",
        scope: "2025-2026: Salt dome identification remains challenging but critical for exploration success."
      },
      {
        id: 168,
        title: "Microseismic Event Locator",
        category: "Geophysics",
        description: "Develop an AI system that precisely locates microseismic events in real-time, improving monitoring for hydraulic fracturing, geothermal operations, and mining activities.",
        githubLink: "https://github.com/academy/microseismic-locator",
        scope: "2025-2027: Enhanced microseismic monitoring will improve resource recovery and reduce environmental impacts."
      },
      {
        id: 169,
        title: "Gravity Data Inversion AI",
        category: "Geophysics",
        description: "Create a machine learning approach to gravity data inversion that identifies subsurface density distributions with higher resolution and fewer assumptions than conventional methods.",
        githubLink: "https://github.com/academy/gravity-inversion-ai",
        scope: "2025-2026: Improved gravity inversion techniques will enhance subsurface imaging for exploration and engineering."
      },
      {
        id: 170,
        title: "Magnetotelluric Deep Learning Interpreter",
        category: "Geophysics",
        description: "Build a deep learning system that interprets magnetotelluric survey data to map subsurface electrical conductivity for geothermal and mineral exploration.",
        githubLink: "https://github.com/academy/mt-deep-interpreter",
        scope: "2025-2027: Advanced MT interpretation will support clean energy exploration and critical mineral discovery."
      },
      {
        id: 171,
        title: "Quantum Seismic Waveform Analyzer",
        category: "Geophysics",
        description: "Develop a quantum computing application for analyzing complex seismic waveforms and extracting subsurface information beyond the capabilities of classical algorithms.",
        githubLink: "https://github.com/academy/quantum-seismic-analyzer",
        scope: "2026-2027: Quantum computing will enable breakthrough advances in seismic data processing."
      },
      {
        id: 172,
        title: "Ground Penetrating Radar AI",
        category: "Geophysics",
        description: "Create an AI system that enhances ground penetrating radar resolution and interpretation for archaeological sites, utility mapping, and shallow geological investigations.",
        githubLink: "https://github.com/academy/gpr-enhancement-ai",
        scope: "2025-2026: GPR applications will expand with improved resolution and interpretation capabilities."
      },
      {
        id: 173,
        title: "Earthquake Early Warning Network",
        category: "Geophysics",
        description: "Build an AI-powered earthquake early warning system that integrates seismic, GNSS, and IoT sensor data to provide faster and more accurate alerts.",
        githubLink: "https://github.com/academy/quake-early-warning",
        scope: "2025-2027: Improved early warning systems will save lives and protect critical infrastructure."
      },
      {
        id: 174,
        title: "Full Waveform Inversion Optimizer",
        category: "Geophysics",
        description: "Develop an AI approach to optimize full waveform inversion for seismic data, reducing computational requirements while improving subsurface imaging accuracy.",
        githubLink: "https://github.com/academy/fwi-ai-optimizer",
        scope: "2025-2026: FWI optimization will enhance subsurface imaging for exploration and civil engineering."
      },
      {
        id: 175,
        title: "Passive Seismic Tomography System",
        category: "Geophysics",
        description: "Create an AI system that performs tomographic imaging of the subsurface using ambient seismic noise and passive sources for long-term monitoring applications.",
        githubLink: "https://github.com/academy/passive-seismic-tomography",
        scope: "2025-2027: Passive monitoring techniques will grow in importance for reservoir management and hazard monitoring."
      },
      {
        id: 176,
        title: "Electromagnetic Survey Neural Network",
        category: "Geophysics",
        description: "Build a neural network that processes electromagnetic survey data to identify mineral deposits, groundwater resources, and contamination plumes with improved accuracy.",
        githubLink: "https://github.com/academy/em-survey-nn",
        scope: "2025-2026: EM methods will gain importance for critical mineral exploration and environmental monitoring."
      },
      {
        id: 177,
        title: "Quantum Gravity Field Modeler",
        category: "Geophysics",
        description: "Develop a quantum computing application for modeling complex gravity fields and their variations over time for geodetic and exploration applications.",
        githubLink: "https://github.com/academy/quantum-gravity-modeler",
        scope: "2026-2027: Quantum computing will enable new approaches to geophysical field modeling."
      },
      {
        id: 178,
        title: "Seismic Attribute Analysis AI",
        category: "Geophysics",
        description: "Create an AI system that automatically extracts, analyzes, and interprets seismic attributes to identify geological features and reservoir properties.",
        githubLink: "https://github.com/academy/seismic-attribute-ai",
        scope: "2025-2026: Advanced attribute analysis will remain critical for subsurface characterization."
      },
      {
        id: 179,
        title: "Geophysical Data Fusion Platform",
        category: "Geophysics",
        description: "Build a machine learning system that integrates multiple geophysical datasets (seismic, gravity, magnetic, EM) to create enhanced subsurface models.",
        githubLink: "https://github.com/academy/geophysical-fusion",
        scope: "2025-2027: Multi-physics data integration will improve exploration success rates and subsurface monitoring."
      },
      {
        id: 180,
        title: "Advanced Resistivity Inversion AI",
        category: "Geophysics",
        description: "Develop an AI approach to electrical resistivity tomography inversion that handles complex geology and provides uncertainty quantification.",
        githubLink: "https://github.com/academy/resistivity-inversion-ai",
        scope: "2025-2026: Resistivity methods will expand applications in groundwater, geothermal, and environmental monitoring."
      },
      {
        id: 181,
        title: "Ionospheric Tomography System",
        category: "Geophysics",
        description: "Create an AI system for ionospheric tomography using GNSS signals to monitor space weather and improve communication and navigation systems.",
        githubLink: "https://github.com/academy/ionospheric-tomography",
        scope: "2025-2027: Ionospheric monitoring will grow in importance as space-based infrastructure expands."
      },
      {
        id: 182,
        title: "Borehole Geophysics Interpreter",
        category: "Geophysics",
        description: "Build an AI system that interprets multiple borehole geophysical logs simultaneously to characterize formations, identify hydrocarbons, and assess reservoir quality.",
        githubLink: "https://github.com/academy/borehole-geophysics-ai",
        scope: "2025-2026: Well log interpretation will remain fundamental to subsurface characterization."
      },
      {
        id: 183,
        title: "Quantum Reservoir Characterization",
        category: "Geophysics",
        description: "Develop a quantum computing approach to integrate geophysical data for detailed reservoir characterization beyond classical computational limitations.",
        githubLink: "https://github.com/academy/quantum-reservoir-char",
        scope: "2026-2027: Quantum computing will enable comprehensive integration of massive geophysical datasets."
      },
      {
        id: 184,
        title: "Surface Wave Analysis Platform",
        category: "Geophysics",
        description: "Create an AI system that analyzes surface wave propagation in seismic data to determine near-surface properties and identify hazards for construction projects.",
        githubLink: "https://github.com/academy/surface-wave-analyzer",
        scope: "2025-2026: Near-surface characterization will grow in importance for infrastructure development."
      },
      {
        id: 185,
        title: "Geophysical Inversion Neural Network",
        category: "Geophysics",
        description: "Build a neural network approach to geophysical inversion problems that reduces computational requirements and handles nonlinearity and non-uniqueness.",
        githubLink: "https://github.com/academy/geophysical-inversion-nn",
        scope: "2025-2027: AI-driven inversion will transform the speed and accuracy of geophysical processing."
      },
      {
        id: 186,
        title: "4D Seismic Monitoring System",
        category: "Geophysics",
        description: "Develop an AI platform for processing and interpreting time-lapse (4D) seismic data to monitor fluid movement in reservoirs and geological carbon storage sites.",
        githubLink: "https://github.com/academy/4d-seismic-monitor",
        scope: "2025-2026: Time-lapse monitoring will be essential for optimizing resource recovery and carbon storage."
      },
      {
        id: 187,
        title: "Marine CSEM Interpretation AI",
        category: "Geophysics",
        description: "Create a machine learning system for interpreting marine controlled-source electromagnetic survey data to identify hydrocarbon reservoirs and map resistivity structures.",
        githubLink: "https://github.com/academy/marine-csem-ai",
        scope: "2025-2027: CSEM methods will complement seismic for offshore exploration and monitoring."
      },
      {
        id: 188,
        title: "Geothermal Resource AI Finder",
        category: "Geophysics",
        description: "Build an AI system that integrates geophysical, geological, and thermal data to identify and characterize geothermal resources for clean energy development.",
        githubLink: "https://github.com/academy/geothermal-finder-ai",
        scope: "2025-2027: Geothermal energy development will accelerate with improved exploration technologies."
      },
      {
        id: 189,
        title: "Urban Geophysics Platform",
        category: "Geophysics",
        description: "Develop an AI-powered platform for urban geophysical surveys that identifies subsurface infrastructure, voids, and hazards while filtering anthropogenic noise.",
        githubLink: "https://github.com/academy/urban-geophysics-platform",
        scope: "2025-2026: Urban subsurface mapping will become essential for smart city development and infrastructure management."
      },
      {
        id: 190,
        title: "Quantum Seismic Processing System",
        category: "Geophysics",
        description: "Create a quantum computing application for seismic data processing that handles complex signal processing tasks beyond classical computational capabilities.",
        githubLink: "https://github.com/academy/quantum-seismic-processor",
        scope: "2026-2027: Quantum computing will revolutionize computationally intensive geophysical processing."
      },
      {
        id: 191,
        title: "AI Drilling Optimization System",
        category: "Oil Industry",
        description: "Develop an AI system that optimizes drilling parameters in real-time based on formation characteristics, historical performance, and current drilling conditions.",
        githubLink: "https://github.com/academy/ai-drilling-optimizer",
        scope: "2025-2026: Advanced drilling optimization will reduce costs and environmental impact in hydrocarbon production."
      },
      {
        id: 192,
        title: "Reservoir Performance Predictor",
        category: "Oil Industry",
        description: "Create a machine learning system that predicts reservoir performance based on production history, well tests, and reservoir properties to optimize field development.",
        githubLink: "https://github.com/academy/reservoir-performance-ai",
        scope: "2025-2027: Enhanced production optimization will be critical for maximizing economic recovery."
      },
      {
        id: 193,
        title: "Digital Twin for Oil Fields",
        category: "Oil Industry",
        description: "Build a comprehensive digital twin platform that integrates real-time data with AI models to simulate and optimize all aspects of oilfield operations.",
        githubLink: "https://github.com/academy/oilfield-digital-twin",
        scope: "2025-2027: Digital twins will transform operational efficiency and decision-making in energy production."
      },
      {
        id: 194,
        title: "Quantum Reservoir Simulation",
        category: "Oil Industry",
        description: "Develop a quantum computing approach to reservoir simulation that models complex fluid dynamics and heterogeneous geological properties beyond classical capabilities.",
        githubLink: "https://github.com/academy/quantum-reservoir-sim",
        scope: "2026-2027: Quantum computing will enable unprecedented detail in reservoir modeling."
      },
      {
        id: 195,
        title: "Well Log Interpretation AI",
        category: "Oil Industry",
        description: "Create a deep learning system that automatically interprets multiple well log types to identify formation boundaries, lithology, fluid contacts, and reservoir properties.",
        githubLink: "https://github.com/academy/well-log-interpreter-ai",
        scope: "2025-2026: Automated log interpretation will improve efficiency and consistency in reservoir characterization."
      },
      {
        id: 196,
        title: "Production Optimization AI",
        category: "Oil Industry",
        description: "Build an AI system that optimizes production parameters across multiple wells to maximize recovery while minimizing costs and environmental impact.",
        githubLink: "https://github.com/academy/production-optimizer-ai",
        scope: "2025-2027: Production optimization will remain critical for field economics and environmental performance."
      },
      {
        id: 197,
        title: "Enhanced Oil Recovery AI",
        category: "Oil Industry",
        description: "Develop a machine learning system that optimizes enhanced oil recovery processes (chemical, thermal, gas) based on reservoir characteristics and production history.",
        githubLink: "https://github.com/academy/eor-optimizer-ai",
        scope: "2025-2026: EOR will grow in importance as conventional production declines in mature basins."
      },
      {
        id: 198,
        title: "Predictive Maintenance for Oil Assets",
        category: "Oil Industry",
        description: "Create an AI platform that predicts equipment failures and maintenance needs for oil production, processing, and transportation infrastructure.",
        githubLink: "https://github.com/academy/oil-predictive-maintenance",
        scope: "2025-2027: Predictive maintenance will reduce downtime and extend asset life in the energy sector."
      },
      {
        id: 199,
        title: "Methane Emission Detection System",
        category: "Oil Industry",
        description: "Build an AI system that detects, quantifies, and locates methane emissions from oil and gas infrastructure using satellite, drone, and ground-based sensors.",
        githubLink: "https://github.com/academy/methane-detection-ai",
        scope: "2025-2027: Methane emission reduction will be critical for improving the environmental performance of oil and gas operations."
      },
      {
        id: 200,
        title: "Quantum Computing for Fluid Flow",
        category: "Oil Industry",
        description: "Develop a quantum computing application for modeling complex multiphase fluid flow in porous media beyond the capabilities of classical simulation approaches.",
        githubLink: "https://github.com/academy/quantum-fluid-flow",
        scope: "2026-2027: Quantum computing will enable new insights into complex subsurface fluid behavior."
      },
      {
        id: 201,
        title: "Unconventional Reservoir Analyzer",
        category: "Oil Industry",
        description: "Create an AI system specialized in characterizing unconventional reservoirs, including fracture networks, organic content, and production potential.",
        githubLink: "https://github.com/academy/unconventional-reservoir-ai",
        scope: "2025-2026: Unconventional resource development will benefit from improved characterization techniques."
      },
      {
        id: 202,
        title: "Refinery Process Optimization AI",
        category: "Oil Industry",
        description: "Build a machine learning system that optimizes refinery processes in real-time to maximize product value, minimize energy consumption, and reduce emissions.",
        githubLink: "https://github.com/academy/refinery-optimization-ai",
        scope: "2025-2027: Refinery efficiency will remain critical for competitiveness and environmental performance."
      },
      {
        id: 203,
        title: "Geosteering Automation System",
        category: "Oil Industry",
        description: "Develop an AI system that automates geosteering decisions during directional drilling based on real-time measurements and geological models.",
        githubLink: "https://github.com/academy/geosteering-automation",
        scope: "2025-2026: Automated geosteering will improve drilling accuracy and reduce costs."
      },
      {
        id: 204,
        title: "Oil Price Forecasting AI",
        category: "Oil Industry",
        description: "Create a machine learning system that forecasts oil prices based on global production, consumption, geopolitical factors, and alternative energy trends.",
        githubLink: "https://github.com/academy/oil-price-forecaster",
        scope: "2025-2027: Price forecasting will be essential for investment planning in the energy transition."
      },
      {
        id: 205,
        title: "Quantum Seismic Processing for Oil",
        category: "Oil Industry",
        description: "Build a quantum computing application for seismic data processing specifically optimized for hydrocarbon exploration challenges.",
        githubLink: "https://github.com/academy/quantum-oil-seismic",
        scope: "2026-2027: Quantum computing will enable new approaches to complex geophysical problems."
      },
      {
        id: 206,
        title: "Carbon Capture Optimization Platform",
        category: "Oil Industry",
        description: "Develop an AI system that optimizes carbon capture, utilization, and storage operations for oil and gas facilities to reduce emissions and meet climate goals.",
        githubLink: "https://github.com/academy/carbon-capture-optimizer",
        scope: "2025-2027: CCUS will become increasingly important for the oil industry's climate strategy."
      },
      {
        id: 207,
        title: "Real-time Drilling Anomaly Detector",
        category: "Oil Industry",
        description: "Create an AI system that identifies drilling anomalies and potential hazards in real-time to prevent blowouts, stuck pipe, and other drilling problems.",
        githubLink: "https://github.com/academy/drilling-anomaly-detector",
        scope: "2025-2026: Improved safety and reduced non-productive time will remain key priorities in drilling operations."
      },
      {
        id: 208,
        title: "Field Development Optimizer",
        category: "Oil Industry",
        description: "Build an AI platform that optimizes field development plans, including well placement, facilities design, and production scheduling for maximum economic return.",
        githubLink: "https://github.com/academy/field-development-optimizer",
        scope: "2025-2027: Optimized field development will be essential for economic and environmental performance."
      },
      {
        id: 209,
        title: "Oil Transportation Logistics AI",
        category: "Oil Industry",
        description: "Develop a machine learning system that optimizes oil transportation logistics, including pipeline, rail, and maritime shipping to minimize costs and emissions.",
        githubLink: "https://github.com/academy/oil-logistics-ai",
        scope: "2025-2026: Logistics optimization will improve efficiency and reduce environmental impact."
      },
      {
        id: 210,
        title: "Reservoir Uncertainty Quantifier",
        category: "Oil Industry",
        description: "Create an AI approach to quantifying uncertainties in reservoir models and production forecasts to improve decision-making under uncertainty.",
        githubLink: "https://github.com/academy/reservoir-uncertainty-ai",
        scope: "2025-2027: Uncertainty quantification will improve risk management in energy production."
      },
      {
        id: 211,
        title: "Enhanced Oil Analysis Laboratory",
        category: "Oil Industry",
        description: "Build an AI system that automates oil sample analysis to identify composition, contamination, and degradation for improved equipment maintenance and process control.",
        githubLink: "https://github.com/academy/oil-analysis-ai",
        scope: "2025-2026: Advanced oil analysis will improve equipment reliability and performance."
      },
      {
        id: 212,
        title: "Energy Asset Management Platform",
        category: "Oil Industry",
        description: "Develop an AI-powered platform for managing oil and gas assets throughout their lifecycle, including predictive maintenance, performance optimization, and decommissioning planning.",
        githubLink: "https://github.com/academy/energy-asset-manager",
        scope: "2025-2027: Intelligent asset management will maximize value and minimize environmental impact."
      },
      {
        id: 213,
        title: "Petroleum Supply Chain Optimizer",
        category: "Oil Industry",
        description: "Create a machine learning system that optimizes the entire petroleum supply chain from wellhead to consumer, including production, processing, distribution, and retail.",
        githubLink: "https://github.com/academy/petroleum-supply-chain",
        scope: "2025-2026: Supply chain optimization will improve efficiency and reduce carbon intensity."
      },
      {
        id: 214,
        title: "Quantum Energy Infrastructure Modeling",
        category: "Oil Industry",
        description: "Build a quantum computing application for modeling complex energy infrastructure networks, including production, processing, and distribution systems.",
        githubLink: "https://github.com/academy/quantum-energy-infrastructure",
        scope: "2026-2027: Quantum computing will enable comprehensive modeling of complex energy systems."
      },
      {
        id: 215,
        title: "Real-time Production Forecaster",
        category: "Oil Industry",
        description: "Develop an AI system that provides real-time production forecasts based on current well conditions, historical performance, and reservoir characteristics.",
        githubLink: "https://github.com/academy/production-forecaster-rt",
        scope: "2025-2027: Real-time forecasting will enable adaptive production strategies and improve operational efficiency."
      },
      {
        id: 216,
        title: "Smart Grid Energy Management",
        category: "Energy",
        description: "Create an AI platform that optimizes electricity distribution through smart grids, balancing supply and demand while integrating renewable energy sources.",
        githubLink: "https://github.com/academy/smart-grid-energy-mgmt",
        scope: "2025-2027: Smart grid technologies will be essential for managing distributed energy resources."
      },
      {
        id: 217,
        title: "Renewable Energy Forecasting System",
        category: "Energy",
        description: "Build a machine learning system that forecasts solar and wind energy production based on weather patterns, historical data, and equipment performance.",
        githubLink: "https://github.com/academy/renewable-forecasting-ai",
        scope: "2025-2026: Accurate renewable forecasting will be critical for grid stability and energy trading."
      },
      {
        id: 218,
        title: "Energy Storage Optimization Platform",
        category: "Energy",
        description: "Develop an AI system that optimizes energy storage operations, including charging/discharging cycles, for maximum economic and grid stability benefits.",
        githubLink: "https://github.com/academy/energy-storage-optimizer",
        scope: "2025-2027: Energy storage optimization will be essential for renewable integration and grid resilience."
      },
      {
        id: 219,
        title: "Quantum Grid Optimization",
        category: "Energy",
        description: "Create a quantum computing application for optimizing complex power grid operations beyond classical computational capabilities.",
        githubLink: "https://github.com/academy/quantum-grid-optimizer",
        scope: "2026-2027: Quantum computing will revolutionize complex grid optimization problems."
      },
      {
        id: 220,
        title: "Digital Twin for Power Plants",
        category: "Energy",
        description: "Build a comprehensive digital twin platform for power generation plants that simulates operations, predicts maintenance needs, and optimizes efficiency.",
        githubLink: "https://github.com/academy/power-plant-digital-twin",
        scope: "2025-2026: Digital twins will transform power plant management and performance."
      },
      {
        id: 221,
        title: "Microgrid Controller AI",
        category: "Energy",
        description: "Develop an AI system that controls and optimizes microgrids with multiple energy sources, storage systems, and loads for maximum reliability and efficiency.",
        githubLink: "https://github.com/academy/microgrid-controller-ai",
        scope: "2025-2027: Microgrids will grow in importance for resilience and renewable integration."
      },
      {
        id: 222,
        title: "Energy Demand Response Platform",
        category: "Energy",
        description: "Create an AI platform that optimizes demand response programs, automatically adjusting energy consumption based on grid conditions and price signals.",
        githubLink: "https://github.com/academy/demand-response-platform",
        scope: "2025-2026: Demand response will play a growing role in grid management and decarbonization."
      },
      {
        id: 223,
        title: "Hydrogen Production Optimizer",
        category: "Energy",
        description: "Build a machine learning system that optimizes green hydrogen production from renewable energy, considering electricity prices, equipment efficiency, and demand.",
        githubLink: "https://github.com/academy/hydrogen-optimizer",
        scope: "2025-2027: Hydrogen will become increasingly important as an energy carrier and storage medium."
      },
      {
        id: 224,
        title: "Energy Market Trading AI",
        category: "Energy",
        description: "Develop an AI system that optimizes energy trading strategies in wholesale markets, considering price forecasts, generation capabilities, and risk management.",
        githubLink: "https://github.com/academy/energy-trading-ai",
        scope: "2025-2026: Automated trading will improve market efficiency and asset value."
      },
      {
        id: 225,
        title: "Quantum Chemical Process Optimizer",
        category: "Energy",
        description: "Create a quantum computing application for optimizing complex chemical processes in energy production, including refining, petrochemicals, and biofuels.",
        githubLink: "https://github.com/academy/quantum-chemical-optimizer",
        scope: "2026-2027: Quantum computing will enable breakthrough efficiency improvements in chemical processes."
      },
      {
        id: 226,
        title: "Carbon Emission Tracking Platform",
        category: "Energy",
        description: "Build an AI platform that tracks carbon emissions across energy supply chains, enabling accurate accounting and targeted reduction strategies.",
        githubLink: "https://github.com/academy/carbon-tracking-platform",
        scope: "2025-2027: Carbon accounting will be essential for energy companies in a carbon-constrained world."
      },
      {
        id: 227,
        title: "Energy Infrastructure Vulnerability Assessment",
        category: "Energy",
        description: "Develop an AI system that identifies vulnerabilities in energy infrastructure to physical and cyber threats, natural disasters, and climate change impacts.",
        githubLink: "https://github.com/academy/energy-vulnerability-assessment",
        scope: "2025-2026: Infrastructure resilience will grow in importance as climate risks increase."
      },
      {
        id: 228,
        title: "Solar Farm Optimization System",
        category: "Energy",
        description: "Create a machine learning system that optimizes solar farm operations, including panel orientation, cleaning schedules, and maintenance priorities.",
        githubLink: "https://github.com/academy/solar-farm-optimizer",
        scope: "2025-2027: Solar energy will continue rapid growth, requiring advanced optimization techniques."
      },
      {
        id: 229,
        title: "Nuclear Plant Safety Monitor",
        category: "Energy",
        description: "Build an AI platform for monitoring nuclear power plant operations, identifying potential safety issues, and optimizing maintenance schedules.",
        githubLink: "https://github.com/academy/nuclear-safety-monitor",
        scope: "2025-2026: Nuclear safety will remain a critical concern as plants age and new designs emerge."
      },
      {
        id: 230,
        title: "Quantum Materials Discovery for Energy",
        category: "Energy",
        description: "Develop a quantum computing application for discovering new materials for energy applications, including batteries, solar cells, and catalysts.",
        githubLink: "https://github.com/academy/quantum-energy-materials",
        scope: "2026-2027: Quantum computing will accelerate materials discovery for energy technologies."
      },
      {
        id: 231,
        title: "Energy Efficiency Recommendation Engine",
        category: "Energy",
        description: "Create an AI system that analyzes energy consumption patterns and provides personalized recommendations for improving efficiency in buildings and industrial processes.",
        githubLink: "https://github.com/academy/energy-efficiency-recommender",
        scope: "2025-2027: Energy efficiency will remain the most cost-effective decarbonization strategy."
      },
      {
        id: 232,
        title: "Thermal Energy Storage Optimizer",
        category: "Energy",
        description: "Build a machine learning system that optimizes thermal energy storage systems for district heating, industrial processes, and concentrated solar power.",
        githubLink: "https://github.com/academy/thermal-storage-optimizer",
        scope: "2025-2026: Thermal energy storage will grow in importance for sector coupling and renewable integration."
      },
      {
        id: 233,
        title: "Electric Vehicle Charging Network Planner",
        category: "Energy",
        description: "Develop an AI platform that optimizes the placement and operation of electric vehicle charging infrastructure based on traffic patterns, grid capacity, and usage projections.",
        githubLink: "https://github.com/academy/ev-charging-planner",
        scope: "2025-2027: EV charging infrastructure will expand rapidly to support transportation electrification."
      },
      {
        id: 234,
        title: "Geothermal Power Optimization System",
        category: "Energy",
        description: "Create an AI system that optimizes geothermal power plant operations, including well management, fluid circulation, and maintenance scheduling.",
        githubLink: "https://github.com/academy/geothermal-optimizer",
        scope: "2025-2026: Geothermal energy will grow in importance as a baseload renewable resource."
      },
      {
        id: 235,
        title: "Energy System Digital Twin",
        category: "Energy",
        description: "Build a comprehensive digital twin platform for regional or national energy systems, integrating generation, transmission, distribution, and consumption models.",
        githubLink: "https://github.com/academy/energy-system-digital-twin",
        scope: "2025-2027: System-level modeling will be critical for energy transition planning."
      },
      {
        id: 236,
        title: "Wind Farm Wake Effect Optimizer",
        category: "Energy",
        description: "Develop a machine learning system that optimizes wind turbine operations to minimize wake effects and maximize overall farm output.",
        githubLink: "https://github.com/academy/wind-wake-optimizer",
        scope: "2025-2026: Wind energy optimization will improve economics and grid integration."
      },
      {
        id: 237,
        title: "Quantum Power Flow Simulator",
        category: "Energy",
        description: "Create a quantum computing application for simulating complex power flow scenarios in large-scale transmission networks beyond classical computational limits.",
        githubLink: "https://github.com/academy/quantum-power-flow",
        scope: "2026-2027: Quantum computing will enable comprehensive grid modeling for planning and operations."
      },
      {
        id: 238,
        title: "Energy Consumption Forecasting AI",
        category: "Energy",
        description: "Build an AI system that forecasts energy consumption across different sectors and regions based on economic indicators, weather patterns, and technological trends.",
        githubLink: "https://github.com/academy/energy-consumption-forecaster",
        scope: "2025-2027: Accurate demand forecasting will be essential for energy system planning."
      },
      {
        id: 239,
        title: "Hydroelectric Dam Optimization Platform",
        category: "Energy",
        description: "Develop a machine learning system that optimizes hydroelectric dam operations, balancing power generation, water management, and environmental concerns.",
        githubLink: "https://github.com/academy/hydroelectric-optimizer",
        scope: "2025-2026: Hydropower optimization will improve both energy and water resource management."
      },
      {
        id: 240,
        title: "Energy Trading Fraud Detection",
        category: "Energy",
        description: "Create an AI system that detects fraudulent or manipulative trading activities in energy markets through pattern recognition and anomaly detection.",
        githubLink: "https://github.com/academy/energy-fraud-detector",
        scope: "2025-2027: Market integrity will remain essential for efficient energy markets."
      },
      {
        id: 241,
        title: "Coal Plant Emissions Reduction AI",
        category: "Energy",
        description: "Build a machine learning system that optimizes coal power plant operations to minimize emissions while maintaining reliability and efficiency.",
        githubLink: "https://github.com/academy/coal-emissions-reducer",
        scope: "2025-2026: Emissions reduction from existing coal plants will remain important during energy transition."
      },
      {
        id: 242,
        title: "Bioenergy Crop Optimization Platform",
        category: "Energy",
        description: "Develop an AI platform that optimizes bioenergy crop selection, cultivation, and harvesting based on soil conditions, climate, and energy conversion efficiency.",
        githubLink: "https://github.com/academy/bioenergy-crop-optimizer",
        scope: "2025-2027: Sustainable bioenergy will play a growing role in the energy mix."
      },
      {
        id: 243,
        title: "Building Energy Management System",
        category: "Energy",
        description: "Create an AI system that optimizes building energy usage by controlling HVAC, lighting, and appliances based on occupancy, weather, and energy prices.",
        githubLink: "https://github.com/academy/building-energy-manager",
        scope: "2025-2026: Building energy management will be critical for decarbonization and cost savings."
      },

  ];

  // Initialize projects and set up intersection observer for animations
  useEffect(() => {
    setProjects(allProjects);
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const projectId = parseInt(entry.target.getAttribute('data-project-id'));
            setVisibleProjects(prev => [...prev, projectId]);
          }
        });
      },
      { threshold: 0.1 }
    );
    
    // Set up refs when component mounts and observer is ready
    setTimeout(() => {
      projectRefs.current = document.querySelectorAll('.project-card');
      projectRefs.current.forEach(ref => {
        if (ref) observer.observe(ref);
      });
    }, 500);
    
    return () => {
      if (projectRefs.current.length) {
        projectRefs.current.forEach(ref => {
          if (ref) observer.unobserve(ref);
        });
      }
    };
  }, []);

  // Filter projects based on category and search term
  const filteredProjects = projects.filter(project => {
    const matchesCategory = activeCategory === 'All' || project.category === activeCategory;
    const matchesSearch = project.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         project.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Handle category change
  const handleCategoryChange = (category) => {
    setActiveCategory(category);
    setVisibleProjects([]);
    
    // Reset intersection observer after filter change
    setTimeout(() => {
      projectRefs.current = document.querySelectorAll('.project-card');
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const projectId = parseInt(entry.target.getAttribute('data-project-id'));
              setVisibleProjects(prev => [...prev, projectId]);
            }
          });
        },
        { threshold: 0.1 }
      );
      
      projectRefs.current.forEach(ref => {
        if (ref) observer.observe(ref);
      });
    }, 100);
  };

  // Distribute projects into three columns
  const distributeProjects = () => {
    const columns = [[], [], []];
    filteredProjects.forEach((project, index) => {
      columns[index % 3].push(project);
    });
    return columns;
  };

  const projectColumns = distributeProjects();

  // Animation variants for framer-motion
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.1 
      } 
    }
  };

  const projectVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { 
        type: "spring", 
        stiffness: 100,
        damping: 12
      } 
    }
  };

  return (
    <Container fluid className="projects-showcase py-5 my-5">
      <Row className="mb-5">
        <Col>
          <motion.h1 
            className="text-center main-title"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            Future-Ready Tech Projects
          </motion.h1>
          <motion.p 
            className="text-center subtitle"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Comprehensive hands-on projects designed for high-demand careers in 2025-2028
          </motion.p>
        </Col>
      </Row>
      
      <Row className="mb-4">
        <Col md={8} className="mx-auto">
          <div className="search-filter-container">
            <div className="search-box">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search projects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
            
            <Button 
              variant="outline-primary"
              className="filter-toggle-btn"
              onClick={() => setShowFilters(!showFilters)}
            >
              <FaFilter className="me-2" />
              Filter
              {showFilters ? <FaChevronUp className="ms-2" /> : <FaChevronDown className="ms-2" />}
            </Button>
          </div>
          
          <motion.div 
            className="categories-container"
            initial={{ height: 0, opacity: 0 }}
            animate={{ 
              height: showFilters ? 'auto' : 0,
              opacity: showFilters ? 1 : 0
            }}
            transition={{ duration: 0.3 }}
          >
            <div className="categories-wrapper">
              {categories.map(category => (
                <Button
                  key={category}
                  variant={activeCategory === category ? "primary" : "outline-secondary"}
                  className="category-btn m-1"
                  onClick={() => handleCategoryChange(category)}
                >
                  {category}
                </Button>
              ))}
            </div>
          </motion.div>
        </Col>
      </Row>
      
      <Row>
        {projectColumns.map((column, colIndex) => (
          <Col md={4} key={colIndex}>
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="project-column"
            >
              {column.map(project => (
                <motion.div
                  key={project.id}
                  className="project-card mb-4"
                  data-project-id={project.id}
                  variants={projectVariants}
                  initial="hidden"
                  animate={visibleProjects.includes(project.id) ? "visible" : "hidden"}
                >
                  <div className="project-header">
                    <h3 className="project-title">{project.title}</h3>
                    <span className="project-category">{project.category}</span>
                  </div>
                  
                  <Accordion>
                    <Accordion.Item eventKey="0">
                      <div className="project-actions">
                        {/* <a 
                          href={project.githubLink} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="github-link"
                        >
                          <FaGithub size={18} /> GitHub Repo
                        </a> */}
                        
                        <Accordion.Header as="div">
                          <Button variant="success" size="md" className="show-description-btn w-100 d-flex mx-auto align-content-center justify-content-center">
                            Show Description
                          </Button>
                        </Accordion.Header>
                      </div>
                      
                      <Accordion.Body>
                        <div className="project-description">
                          <p>{project.description}</p>
                          <div className="future-scope">
                            <strong>Future Scope:</strong>
                            <p>{project.scope}</p>
                          </div>
                        </div>
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>
                </motion.div>
              ))}
            </motion.div>
          </Col>
        ))}
      </Row>
      
      {filteredProjects.length === 0 && (
        <Row className="mt-5">
          <Col className="text-center">
            <div className="no-results">
              <h3>No projects found</h3>
              <p>Try adjusting your search or filter criteria</p>
            </div>
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default ProjectsShowcase;